<template>
  <div class="group-chat" :class="viewClass" :style="detachedStyle">
    <SupportVideoPlayer
      v-model="supportVideoOpen"
      :title="supportVideoTitle"
      :source-url="supportVideoSourceUrl"
      :get-playback="supportVideoPlaybackRequest"
    />

    <!-- Header bar — always visible -->
    <div class="group-chat__header" @pointerdown="startDetachedDrag">
      <span
        class="group-chat__title"
        :class="{ 'group-chat__title--flash': newMessageFlash }"
      >
        {{ chatTitle }}
      </span>
      <q-space />
      <q-badge
        v-if="viewState === 'collapsed' && unreadCount > 0"
        color="red"
        floating
        :label="unreadCount"
        class="q-mr-sm"
      />
      <q-btn
        v-if="detached || viewState !== 'collapsed'"
        flat
        dense
        round
        :icon="detached ? 'keyboard_return' : 'open_in_new'"
        size="sm"
        :title="detached ? 'Dock chat' : 'Pop out chat'"
        @pointerdown.stop
        @click.stop="toggleDetached"
      />
      <q-btn
        v-if="viewState !== 'collapsed'"
        flat
        dense
        round
        icon="remove"
        size="sm"
        title="Minimize"
        @pointerdown.stop
        @click.stop="viewState = 'collapsed'"
      />
      <q-btn
        flat
        dense
        round
        :icon="headerIcon"
        size="sm"
        :title="
          viewState === 'collapsed'
            ? 'Open'
            : viewState === 'mini'
            ? 'Expand'
            : 'Shrink'
        "
        @pointerdown.stop
        @click.stop="cycleState"
      />
    </div>

    <!-- Viewer role notice -->
    <div v-show="viewState !== 'collapsed'" class="group-chat__banner">
      This chat is not visible to users with &ldquo;viewer&rdquo; role
    </div>

    <div v-show="viewState !== 'collapsed'" class="group-chat__conversations">
      <q-select
        v-model="activeConversationId"
        dense
        outlined
        emit-value
        map-options
        :options="conversationOptions"
        popup-content-class="group-chat__select-menu"
        class="group-chat__conversation-select"
      />
      <div class="group-chat__conversation-actions">
        <q-btn
          dense
          flat
          icon="block"
          title="Blocked users"
          aria-label="Blocked users"
          @click="openBlockedUsers"
        />
        <q-btn
          dense
          flat
          icon="person_add"
          title="New private chat"
          @click="toggleNewConversation"
        />
        <q-btn
          dense
          flat
          icon="smart_toy"
          title="Propipe AI chat"
          @click="createPropipeAiConversation"
        />
        <q-btn
          v-if="canLeaveActiveConversation"
          dense
          flat
          icon="logout"
          title="Leave conversation"
          @click="openLeaveConversationDialog"
        />
      </div>
      <div v-if="showNewConversation" class="group-chat__new-chat">
        <q-select
          v-model="selectedParticipantIds"
          dense
          outlined
          multiple
          emit-value
          map-options
          options-dense
          placeholder="Select people"
          :display-value="selectedParticipantDisplay"
          :options="participantOptions"
          popup-content-class="group-chat__select-menu group-chat__select-menu--participants"
          class="group-chat__participant-select"
        />
        <q-btn
          dense
          color="primary"
          label="Start"
          :disable="selectedParticipantIds.length === 0"
          @click="createPrivateConversation"
        />
      </div>
    </div>

    <!-- Messages + input — hidden when collapsed -->
    <div
      v-show="viewState !== 'collapsed'"
      ref="messageList"
      class="group-chat__messages"
      @scroll="onScroll"
    >
      <div v-if="loadingOlder" class="group-chat__loading">
        Loading older messages…
      </div>
      <div v-if="loading" class="group-chat__loading">Loading…</div>
      <div v-if="!loading && messages.length === 0" class="group-chat__empty">
        No chat history yet
      </div>
      <div
        v-for="msg in messages"
        :key="msg.id"
        class="group-chat__bubble"
        :class="{
          'group-chat__bubble--event':
            msg.messageType === GroupChatMessageType.UserLeft,
          'group-chat__bubble--own': msg.userId === currentUserId,
          'group-chat__bubble--bot': msg.userId === 'system|propipe-ai',
        }"
      >
        <template v-if="msg.messageType === GroupChatMessageType.UserLeft">
          <q-icon name="logout" size="14px" class="q-mr-xs" />
          <span>{{ msg.text }}</span>
          <span class="group-chat__event-time">{{
            formatTime(msg.timestamp)
          }}</span>
        </template>
        <template v-else>
          <div class="group-chat__meta">
            <span
              v-if="msg.userId === 'system|propipe-ai'"
              class="group-chat__author group-chat__author--bot"
            >
              <img src="@/assets/ai-brain.svg" class="group-chat__bot-icon" />
              Propipe AI
            </span>
            <span v-else class="group-chat__author">{{ msg.userName }}</span>
            <span class="group-chat__time">{{
              formatTime(msg.timestamp)
            }}</span>
            <q-btn
              v-if="canModerateMessage(msg)"
              flat
              dense
              round
              icon="more_vert"
              size="xs"
              color="grey-6"
              class="group-chat__delete"
              title="Message actions"
              aria-label="Message actions"
            >
              <q-menu>
                <q-list dense>
                  <q-item
                    v-if="msg.userId === currentUserId"
                    v-close-popup
                    clickable
                    @click="requestDelete(msg.id)"
                  >
                    <q-item-section avatar
                      ><q-icon name="delete_outline"
                    /></q-item-section>
                    <q-item-section>Delete</q-item-section>
                  </q-item>
                  <template v-else>
                    <q-item
                      v-if="msg.userId !== 'system|propipe-ai'"
                      v-close-popup
                      clickable
                      @click="openReport(msg)"
                    >
                      <q-item-section avatar
                        ><q-icon name="flag"
                      /></q-item-section>
                      <q-item-section>Report</q-item-section>
                    </q-item>
                    <q-item
                      v-if="!msg.userId.startsWith('system|')"
                      v-close-popup
                      clickable
                      @click="openBlock(msg)"
                    >
                      <q-item-section avatar
                        ><q-icon name="block"
                      /></q-item-section>
                      <q-item-section>Block user</q-item-section>
                    </q-item>
                  </template>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
          <div v-if="msg.text" class="group-chat__text">
            <template v-if="msg.userId === 'system|propipe-ai'">
              <span
                class="group-chat__markdown"
                v-html="renderMarkdown(msg.text)"
              ></span>
            </template>
            <template v-else>
              <template
                v-for="(segment, index) in tokenizeMessageMentions(msg.text)"
                :key="`${msg.id}-${index}`"
              >
                <span
                  v-if="segment.isMention"
                  class="group-chat__mention"
                  :class="mentionClass(segment.target)"
                >
                  {{ segment.text }}
                </span>
                <span v-else>{{ segment.text }}</span>
              </template>
            </template>
          </div>

          <div
            v-if="
              msg.userId === 'system|propipe-ai' &&
              msg.citations &&
              msg.citations.length > 0
            "
            class="group-chat__sources"
          >
            <span class="group-chat__sources-label">Sources</span>
            <q-btn
              v-for="citation in msg.citations"
              :key="`${msg.id}-${citation.referenceId}`"
              flat
              dense
              no-caps
              icon="open_in_new"
              :label="formatCitationLabel(citation)"
              class="group-chat__source-button"
              title="Open support document"
              @click="openSupportSource(msg.id, citation)"
            />
          </div>

          <!--
            Proposed device commands. Propipe AI cannot send anything: it reads
            the database through a read-only account and can only suggest. The
            settings below are not in the AI's message — they are read from the
            devices when the card is opened, so what is listed is what would
            actually be written, including the settings nobody asked about.
          -->
          <div
            v-for="proposal in visibleProposals(msg)"
            :key="`${msg.id}-${proposal.actionId}`"
            class="group-chat__action"
          >
            <div class="group-chat__action-header">
              <q-icon name="settings_remote" size="16px" />
              <span class="group-chat__action-title">Suggested command</span>
              <q-badge
                outline
                :color="
                  describeProposalTransport(proposal) === 'Iridium'
                    ? 'orange-8'
                    : 'blue-7'
                "
                :label="describeProposalTransport(proposal)"
              />
            </div>

            <div class="group-chat__action-summary">{{ proposal.summary }}</div>

            <div
              v-if="isProposalExecuted(msg, proposal)"
              class="group-chat__action-state group-chat__action-state--sent"
            >
              <q-icon name="check_circle" size="14px" /> Sent
            </div>

            <div
              v-else-if="isProposalCancelled(msg, proposal)"
              class="group-chat__action-state group-chat__action-state--cancelled"
            >
              <q-icon name="cancel" size="14px" /> Canceled
            </div>

            <div
              v-else-if="isProposalExpired(proposal)"
              class="group-chat__action-state group-chat__action-state--expired"
            >
              <q-icon name="schedule" size="14px" /> Expired. Ask Propipe AI
              again for an up-to-date suggestion.
            </div>

            <template v-else>
              <!--
                Nothing can be confirmed until the details have been read: the
                confirmation returns the fingerprint of what was on screen, and
                without one the server refuses.
              -->
              <div
                v-if="!proposalDetail(msg, proposal)"
                class="group-chat__action-buttons"
              >
                <q-btn
                  unelevated
                  dense
                  no-caps
                  color="primary"
                  label="Review settings"
                  :loading="loadingDetailKey === actionKey(msg, proposal)"
                  @click="openProposal(msg, proposal)"
                />
                <q-btn
                  flat
                  dense
                  no-caps
                  color="grey-7"
                  label="Cancel"
                  :loading="cancellingActionId === proposal.actionId"
                  :disable="isProposalBusy(msg, proposal)"
                  @click="cancelProposal(msg, proposal)"
                />
              </div>

              <template v-else>
                <div
                  v-for="warning in proposalDetail(msg, proposal).warnings"
                  :key="warning"
                  class="group-chat__action-warning"
                >
                  <q-icon name="warning" size="14px" /> {{ warning }}
                </div>

                <div class="group-chat__action-section">
                  Devices
                  <span class="group-chat__action-hint">
                    unticking one leaves it out; none can be added
                  </span>
                </div>

                <div class="group-chat__action-devices">
                  <div
                    v-for="target in proposalDetail(msg, proposal).targets"
                    :key="target.uid"
                    class="group-chat__action-device"
                  >
                    <q-checkbox
                      dense
                      size="xs"
                      :model-value="isTargetSelected(msg, proposal, target.uid)"
                      :disable="
                        isOnlySelectedTarget(msg, proposal, target.uid) ||
                        isProposalBusy(msg, proposal)
                      "
                      :label="String(target.uid)"
                      @update:model-value="
                        toggleTarget(msg, proposal, target.uid)
                      "
                    />
                    <span
                      v-if="!target.isConnected"
                      class="group-chat__action-hint"
                      >offline</span
                    >
                    <span
                      v-if="target.pendingCommandCount > 0"
                      class="group-chat__action-hint"
                      >{{ target.pendingCommandCount }} queued</span
                    >
                  </div>
                </div>

                <div class="group-chat__action-section">
                  Settings this command writes
                </div>

                <table class="group-chat__action-fields">
                  <tr
                    v-for="field in proposalDetail(msg, proposal).fields"
                    :key="field.fieldKey || field.label"
                    :class="{
                      'group-chat__action-field--carried':
                        field.isCarriedForward,
                    }"
                  >
                    <td class="group-chat__action-field-label">
                      {{ field.label }}
                      <q-icon
                        v-if="field.isCarriedForward"
                        name="lock_outline"
                        size="11px"
                      >
                        <q-tooltip>
                          Not part of the request. This command cannot write
                          less, so the device's own value is written back.
                        </q-tooltip>
                      </q-icon>
                    </td>
                    <td class="group-chat__action-field-current">
                      {{ field.currentDisplay }}
                    </td>
                    <td class="group-chat__action-field-arrow">→</td>
                    <td
                      class="group-chat__action-field-new"
                      :class="{
                        'group-chat__action-field-new--changed':
                          field.isChanged,
                      }"
                    >
                      <q-select
                        v-if="field.choices && field.choices.length > 0"
                        dense
                        outlined
                        emit-value
                        map-options
                        option-value="value"
                        option-label="label"
                        :options="field.choices"
                        :model-value="fieldValue(msg, proposal, field)"
                        :disable="isProposalBusy(msg, proposal)"
                        class="group-chat__action-field-input"
                        @update:model-value="
                          (value) => editField(msg, proposal, field, value)
                        "
                      />
                      <q-input
                        v-else-if="field.editor === 'elfFrequency'"
                        dense
                        outlined
                        type="number"
                        suffix="Hz"
                        debounce="700"
                        :model-value="fieldValue(msg, proposal, field)"
                        :disable="isProposalBusy(msg, proposal)"
                        class="group-chat__action-field-input"
                        @update:model-value="
                          (value) => editField(msg, proposal, field, value)
                        "
                      />
                      <span v-else>{{ field.newDisplay }}</span>
                    </td>
                  </tr>
                </table>

                <div
                  v-if="proposalDetail(msg, proposal).requiresTypedConfirmation"
                  class="group-chat__action-typed"
                >
                  <div class="group-chat__action-warning">
                    This cannot be undone from here. Type SEND to confirm.
                  </div>
                  <q-input
                    v-model="typedConfirmations[actionKey(msg, proposal)]"
                    dense
                    outlined
                    placeholder="SEND"
                    class="group-chat__action-typed-input"
                  />
                </div>

                <div class="group-chat__action-buttons">
                  <q-btn
                    unelevated
                    dense
                    no-caps
                    color="primary"
                    label="Send command"
                    :disable="!canSendProposal(msg, proposal)"
                    :loading="confirmingActionId === proposal.actionId"
                    @click="confirmProposal(msg, proposal)"
                  />
                  <q-btn
                    flat
                    dense
                    no-caps
                    color="grey-7"
                    label="Dismiss"
                    :disable="isProposalBusy(msg, proposal)"
                    @click="dismissProposal(msg, proposal)"
                  />
                  <q-btn
                    flat
                    dense
                    no-caps
                    color="negative"
                    label="Cancel"
                    :loading="cancellingActionId === proposal.actionId"
                    :disable="isProposalBusy(msg, proposal)"
                    @click="cancelProposal(msg, proposal)"
                  />
                </div>
              </template>
            </template>

            <div
              v-if="actionErrors[actionKey(msg, proposal)]"
              class="group-chat__action-error"
            >
              {{ actionErrors[actionKey(msg, proposal)] }}
            </div>
          </div>

          <!-- Attachment -->
          <div v-if="msg.attachment" class="group-chat__attachment">
            <template
              v-if="
                msg.attachment.contentType &&
                msg.attachment.contentType.startsWith('image/')
              "
            >
              <img
                v-show="attachmentUrls[msg.attachment.blobName]"
                :alt="msg.attachment.originalName"
                class="group-chat__attachment-image"
                style="cursor: zoom-in"
                :src="attachmentUrls[msg.attachment.blobName] || ''"
                @click="
                  openImagePreview(
                    msg.attachment.blobName,
                    msg.attachment.originalName
                  )
                "
              />
              <div
                v-if="attachmentErrors[msg.attachment.blobName]"
                class="group-chat__attachment-image-loading"
              >
                <span>{{ attachmentErrors[msg.attachment.blobName] }}</span>
                <q-btn
                  flat
                  dense
                  label="Retry image"
                  @click="retryImageAttachment(msg.attachment.blobName)"
                />
              </div>
              <div
                v-else-if="!attachmentUrls[msg.attachment.blobName]"
                class="group-chat__attachment-image-loading"
              >
                <q-spinner size="16px" /> Loading image...
              </div>
            </template>
            <template v-else>
              <div
                class="group-chat__attachment-file"
                @click="
                  downloadAttachment(
                    msg.attachment.blobName,
                    msg.attachment.originalName
                  )
                "
              >
                <q-icon name="attach_file" size="16px" class="q-mr-xs" />
                <span
                  class="group-chat__attachment-name"
                  :title="msg.attachment.originalName"
                  >{{ msg.attachment.originalName }}</span
                >
                <span class="group-chat__attachment-size">{{
                  formatFileSize(msg.attachment.sizeBytes)
                }}</span>
              </div>
            </template>
          </div>

          <!-- Reactions row -->
          <div
            v-if="getReactions(msg.id).length > 0 || true"
            class="group-chat__reactions"
          >
            <span
              v-for="r in getReactions(msg.id)"
              :key="r.emoji"
              class="group-chat__reaction-chip"
              :class="{
                'group-chat__reaction-chip--mine':
                  r.userIds.includes(currentUserId),
              }"
              :title="r.userIds.length + ' reaction(s)'"
              @click="toggleReaction(msg.id, r.emoji)"
            >
              {{ r.emoji }}
              <span class="group-chat__reaction-count">{{
                r.userIds.length
              }}</span>
            </span>
            <span
              class="group-chat__reaction-add"
              title="Add reaction"
              @click.stop="openEmojiPicker($event, msg.id)"
              >😀+</span
            >
          </div>
        </template>
      </div>

      <!-- Typing indicator -->
      <div v-if="someoneTyping" class="group-chat__typing">
        <span>Someone is typing</span>
        <span class="typing-dots">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </div>

      <!-- AI thinking indicator -->
      <div
        v-if="propipeQueryPending"
        class="group-chat__bubble group-chat__bubble--bot"
      >
        <div class="group-chat__meta">
          <span class="group-chat__author group-chat__author--bot">
            <img src="@/assets/ai-brain.svg" class="group-chat__bot-icon" />
            Propipe AI
          </span>
        </div>
        <div class="group-chat__text group-chat__thinking">
          Thinking<span class="thinking-dot1">.</span
          ><span class="thinking-dot2">.</span
          ><span class="thinking-dot3">.</span>
        </div>
      </div>
    </div>

    <div v-show="viewState !== 'collapsed'" class="group-chat__input">
      <div v-if="chatSuspended" class="text-negative q-mb-sm" role="alert">
        Your chat access is unavailable. Your other features remain available.
      </div>
      <div
        v-if="chatError"
        class="group-chat__feedback text-negative q-mb-sm"
        role="alert"
      >
        <span>{{ chatError }}</span>
        <q-btn
          flat
          dense
          round
          icon="close"
          size="xs"
          aria-label="Dismiss"
          @click="chatError = ''"
        />
      </div>
      <div
        v-if="chatNotice"
        class="group-chat__feedback text-positive q-mb-sm"
        role="status"
      >
        <span>{{ chatNotice }}</span>
        <q-btn
          flat
          dense
          round
          icon="close"
          size="xs"
          aria-label="Dismiss"
          @click="chatNotice = ''"
        />
      </div>
      <!-- Upload preview -->
      <div v-if="pendingAttachment" class="group-chat__upload-preview">
        <q-icon name="attach_file" size="14px" class="q-mr-xs" />
        <span class="group-chat__upload-name">{{
          pendingAttachment.originalName
        }}</span>
        <span class="group-chat__attachment-size">{{
          formatFileSize(pendingAttachment.sizeBytes)
        }}</span>
        <q-btn
          flat
          dense
          round
          icon="close"
          size="xs"
          :disable="sending"
          @click="pendingAttachment = null"
        />
      </div>
      <div v-if="uploading" class="group-chat__upload-preview">
        <q-spinner size="14px" class="q-mr-xs" />
        <span>Uploading...</span>
      </div>
      <div v-if="showAtMenu" class="group-chat__at-menu">
        <div
          v-for="(option, index) in atMenuOptions"
          :key="option.target"
          class="group-chat__at-option"
          :class="{
            'group-chat__at-option--active': atMenuActiveIndex === index,
          }"
          @mousedown.prevent="insertMention(option.target)"
        >
          <img
            src="@/assets/ai-brain.svg"
            style="width: 16px; height: 16px; margin-right: 6px"
          />
          <span
            ><strong>{{ option.mention }}</strong> —
            {{ option.description }}</span
          >
        </div>
      </div>
      <q-input
        ref="chatInput"
        v-model="draft"
        dense
        outlined
        placeholder="Type a message…"
        class="group-chat__field"
        maxlength="2000"
        :disable="chatSuspended"
        :error="isOverLimit"
        :error-message="`Message exceeds ${2000} characters`"
        hide-bottom-space
        @keydown="onInputKeydown"
        @paste="onPaste"
      >
        <template #append>
          <span
            v-if="draft.length > 1800"
            class="group-chat__char-count"
            :class="{ 'group-chat__char-count--over': isOverLimit }"
            >{{ charsRemaining }}</span
          >
          <span
            class="group-chat__emoji-btn"
            title="Insert emoji"
            @click.stop="openInputEmojiPicker"
            >😀</span
          >
          <q-btn
            flat
            dense
            round
            icon="attach_file"
            size="sm"
            title="Attach file"
            aria-label="Attach file"
            :disable="uploading || chatSuspended"
            @click="triggerFileInput"
          />
          <q-btn
            flat
            dense
            round
            icon="send"
            color="primary"
            :disable="
              (!draft.trim() && !pendingAttachment) ||
              uploading ||
              isOverLimit ||
              chatSuspended ||
              !activeConversationId
            "
            @click="send"
          />
        </template>
      </q-input>
      <input
        ref="fileInput"
        type="file"
        style="display: none"
        accept=".jpg,.jpeg,.png,.pdf,.docx,.xlsx,.pptx,.csv,.agm,.onl,.onlv,.agmv,.ofl,.zip,.text,.txt,.md,.json,.xml"
        @change="onFileSelected"
      />
    </div>

    <!-- Image lightbox preview — teleported to body so no parent clipping applies -->
    <Teleport to="body">
      <div
        v-if="lightboxOpen"
        class="group-chat__lightbox-overlay"
        @click="lightboxOpen = false"
      >
        <div class="group-chat__lightbox" @click.stop>
          <img
            :src="lightboxSrc"
            :alt="lightboxAlt"
            class="group-chat__lightbox-img"
          />
          <div v-if="lightboxLoading" class="text-white q-pa-sm">
            <q-spinner size="16px" /> Loading full-resolution image...
          </div>
          <div v-if="lightboxError" class="text-white q-pa-sm">
            {{ lightboxError }}
            <q-btn
              flat
              dense
              label="Retry full-resolution image"
              @click="openImagePreview(lightboxBlobName, lightboxAlt)"
            />
          </div>
          <div class="group-chat__lightbox-actions">
            <q-btn
              flat
              dense
              round
              icon="download"
              color="white"
              title="Download"
              :disable="lightboxLoading || !lightboxOriginalUrl"
              @click="downloadLightboxImage"
            />
            <q-btn
              flat
              dense
              round
              icon="close"
              color="white"
              title="Close"
              @click="lightboxOpen = false"
            />
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Emoji picker popup (shared for reactions and input) -->
    <div
      v-if="emojiPickerOpen"
      class="group-chat__emoji-overlay"
      @click="emojiPickerOpen = false"
    >
      <div
        class="group-chat__emoji-picker"
        :style="{ bottom: emojiPickerBottom + 'px', right: '8px' }"
        @click.stop
      >
        <span
          v-for="e in emojiOptions"
          :key="e"
          class="group-chat__emoji-option"
          @click="onEmojiSelected(e)"
          >{{ e }}</span
        >
      </div>
    </div>

    <q-dialog v-model="reportDialog" :persistent="moderationBusy">
      <q-card style="width: 420px; max-width: 92vw">
        <q-form @submit.prevent="submitReport">
          <q-card-section class="text-h6">Report message</q-card-section>
          <q-card-section class="q-pt-none">
            <q-select
              v-model="reportReason"
              :options="reportReasons"
              emit-value
              map-options
              outlined
              label="Reason"
              :disable="moderationBusy"
            />
            <q-input
              v-model="reportNotes"
              type="textarea"
              outlined
              label="Additional details (optional)"
              maxlength="1000"
              counter
              class="q-mt-md"
              :disable="moderationBusy"
            />
            <div
              v-if="moderationError"
              class="text-negative q-mt-sm"
              role="alert"
            >
              {{ moderationError }}
            </div>
          </q-card-section>
          <q-card-actions align="right">
            <q-btn
              v-close-popup
              flat
              label="Cancel"
              :disable="moderationBusy"
            />
            <q-btn
              type="submit"
              color="primary"
              label="Submit report"
              :disable="!reportReason"
              :loading="moderationBusy"
            />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>

    <q-dialog v-model="blockDialog" :persistent="moderationBusy">
      <q-card style="width: 420px; max-width: 92vw">
        <q-card-section class="text-h6"
          >Block {{ blockTarget?.userName }}?</q-card-section
        >
        <q-card-section class="q-pt-none">
          You will no longer see each other's messages in company chats. Direct
          chat will be unavailable. Other participants can still see those
          messages.
          <div
            v-if="moderationError"
            class="text-negative q-mt-sm"
            role="alert"
          >
            {{ moderationError }}
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" :disable="moderationBusy" />
          <q-btn
            color="negative"
            icon="block"
            label="Block user"
            :loading="moderationBusy"
            @click="confirmBlock"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="blockedUsersDialog" :persistent="moderationBusy">
      <q-card style="width: 420px; max-width: 92vw">
        <q-card-section class="row items-center">
          <div class="text-h6">Blocked users</div>
          <q-space />
          <q-btn
            v-close-popup
            flat
            round
            dense
            icon="close"
            aria-label="Close"
            :disable="moderationBusy"
          />
        </q-card-section>
        <q-card-section
          class="q-pt-none"
          style="max-height: 60vh; overflow: auto"
        >
          <q-linear-progress v-if="blocksLoading" indeterminate />
          <div v-if="blocksError" class="text-negative" role="alert">
            {{ blocksError }}
            <q-btn
              flat
              icon="refresh"
              label="Retry"
              :disable="blocksLoading"
              @click="loadBlocks(blockPage)"
            />
          </div>
          <div v-else-if="!blocksLoading && !blockedUsers.length">
            No blocked users
          </div>
          <q-list separator>
            <q-item v-for="user in blockedUsers" :key="user.userId">
              <q-item-section style="overflow-wrap: anywhere">{{
                user.userName
              }}</q-item-section>
              <q-item-section side
                ><q-btn
                  flat
                  color="primary"
                  label="Unblock"
                  :disable="moderationBusy || blocksLoading"
                  @click="unblockUser(user.userId)"
              /></q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            flat
            round
            icon="chevron_left"
            title="Previous page"
            :disable="blockPage === 0 || blocksLoading || moderationBusy"
            @click="loadBlocks(blockPage - 1)"
          />
          <span
            >{{ blockPage + 1 }} /
            {{ Math.max(1, Math.ceil(blockTotal / 50)) }}</span
          >
          <q-btn
            flat
            round
            icon="chevron_right"
            title="Next page"
            :disable="
              (blockPage + 1) * 50 >= blockTotal ||
              blocksLoading ||
              moderationBusy
            "
            @click="loadBlocks(blockPage + 1)"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="deleteDialog" :persistent="moderationBusy">
      <q-card style="width: 360px; max-width: 92vw">
        <q-card-section class="text-h6">Delete message?</q-card-section>
        <q-card-section class="q-pt-none"
          >The message and its image will be removed from chat.
          <div
            v-if="moderationError"
            class="text-negative q-mt-sm"
            role="alert"
          >
            {{ moderationError }}
          </div></q-card-section
        >
        <q-card-actions align="right"
          ><q-btn
            v-close-popup
            flat
            label="Cancel"
            :disable="moderationBusy" /><q-btn
            color="negative"
            label="Delete"
            :loading="moderationBusy"
            @click="deleteMessage"
        /></q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="leaveConfirmDialog">
      <q-card style="width: 320px; max-width: 90vw">
        <q-toolbar>
          <q-toolbar-title class="text-blue-brand text-bold">
            Leave Conversation
          </q-toolbar-title>
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section>
          <div class="column">
            <div class="col q-pb-sm">Leave {{ leaveConversationLabel }}?</div>
          </div>
          <q-separator />
          <q-card-actions align="center">
            <q-btn v-close-popup class="btn-brand" label="No" no-caps />
            <q-btn
              v-close-popup
              class="btn-brand"
              label="Yes-Leave"
              no-caps
              @click="confirmLeaveConversation"
            />
          </q-card-actions>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, markRaw } from 'vue';
import axios, { CancelTokenSource } from 'axios';
import { marked } from 'marked';
import SupportVideoPlayer from './SupportVideoPlayer.vue';
import GroupChatService, {
  chatErrorMessage,
  chatReportReasons,
  ChatReportReason,
  ChatAttachment,
  GroupChatConversation,
  GroupChatParticipant,
  ProposedDeviceCommand,
  SupportCitation,
  SupportVideoPlayback,
} from '../services/GroupChatService';
import { isPrivateVideoCitation } from '../services/SupportCitationParser';
import { openSupportDocument } from '../services/SupportDocumentWindow';
import {
  describeTransport,
  isProposedCommandExpired,
} from '../services/ProposedActionParser';
import {
  collectAmendments,
  ProposedActionDetail,
  ProposedActionField,
} from '../models/ProposedActionDetail';
import { hasAnyRole } from '@/utils/AuthGuard';
import { getYouTubeEmbedUrl } from '../services/SupportMediaPolicy';
import {
  GroupChatConversationType,
  GroupChatMessageType,
} from '../models/GroupChat';
import {
  AiQueryTarget,
  AiQueryTargetMentionMap,
  AiQueryTargetToMentionMap,
  resolveAiQueryTargetFromText,
} from '../models/AiQueryTarget';

/**
 * Shape returned by GET /chat/messages and pushed via
 * the SignalR `ReceiveChatMessage` event.
 *
 * The `userName` field is resolved server-side from the Users table
 * using the `userId` foreign key, since the user's identity comes
 * from Auth0 where the username is their email address.
 */
export interface IChatMessage {
  /** Server-assigned unique id (GroupChatMessageId) */
  id: number;
  /** Conversation id (GroupChatConversationId) */
  groupChatConversationId?: number | null;
  /** Message type lookup name. */
  messageType?: GroupChatMessageType;
  /** Auth0 user id of the author (email) */
  userId: string;
  /** Display name resolved from Users.Name */
  userName: string;
  /** Message body */
  text: string;
  /** ISO-8601 UTC timestamp */
  timestamp: string;
  /** Optional file attachment metadata */
  attachment?: ChatAttachment | null;
  /** Trusted support citations persisted with a Propipe AI answer. */
  citations?: SupportCitation[];
  /**
   * Device commands the AI suggested. Suggestions only — nothing has been sent.
   */
  proposedCommands?: ProposedDeviceCommand[];
  /** Action ids on this message that have already been confirmed and sent. */
  executedActionIds?: string[];
  cancelledActionIds?: string[];
  /** User message that produced this AI answer. */
  aiQuerySourceMessageId?: number | null;
}

interface IMessageSegment {
  text: string;
  isMention: boolean;
  target: AiQueryTarget | null;
}

/** Maximum message length — matches the NVARCHAR(2000) backend constraint. */
const MAX_MESSAGE_LENGTH = 2000;
const DETACHED_VIEWPORT_MARGIN = 1;
const DETACHED_COLLAPSED_HEIGHT = 44;
const DETACHED_MINI_VIEWPORT_RATIO = 0.5;

// Configure marked for safe inline rendering (no raw HTML passthrough).
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub-flavored markdown (tables, strikethrough, etc.)
});

export default defineComponent({
  name: 'GroupChat',
  components: { SupportVideoPlayer },

  emits: ['update:expanded'],

  data() {
    return {
      GroupChatConversationType,
      GroupChatMessageType,
      /** 'collapsed' = header only, 'mini' = 300px, 'expanded' = full drawer */
      viewState: 'collapsed' as 'collapsed' | 'mini' | 'expanded',
      detached: false,
      detachedPosition: { x: 24, y: 96 },
      detachedDragStart: null as null | {
        pointerId: number;
        pointerX: number;
        pointerY: number;
        panelX: number;
        panelY: number;
      },
      draft: '',
      chatError: '',
      chatNotice: '',
      reportDialog: false,
      reportTarget: null as IChatMessage | null,
      reportReason: null as ChatReportReason | null,
      reportReasons: [...chatReportReasons],
      reportNotes: '',
      moderationBusy: false,
      moderationError: '',
      blockDialog: false,
      blockTarget: null as GroupChatParticipant | null,
      blockedUsersDialog: false,
      blockedUsers: [] as GroupChatParticipant[],
      blockPage: 0,
      blockTotal: 0,
      blocksLoading: false,
      blocksError: '',
      deleteDialog: false,
      deleteMessageId: null as number | null,
      visibilityTimer: null as ReturnType<typeof setInterval> | null,
      reconciling: false,
      componentAlive: true,
      uiGeneration: 0,
      unreadCount: 0,
      /** true when user has scrolled up — prevents auto-scroll hijacking */
      userScrolledUp: false,
      /** tracks previous message count to detect new arrivals */
      previousMessageCount: 0,
      /** prevents flooding typing signals — throttle gate */
      typingThrottled: false,
      /** drives the title text flash on incoming message */
      newMessageFlash: false,
      newMessageFlashTimer: null as ReturnType<typeof setTimeout> | null,
      /** gates animation so it doesn't fire on initial fetch */
      initialLoadComplete: false,
      /** shows the @propipe autocomplete popup */
      showAtMenu: false,
      /** highlighted option index in @ menu */
      atMenuActiveIndex: 0,
      /** currently selected AI destination for this draft */
      selectedAiQueryTarget: null as AiQueryTarget | null,
      showNewConversation: false,
      selectedParticipantIds: [] as string[],
      leaveConfirmDialog: false,
      /** emoji picker state */
      emojiPickerOpen: false,
      emojiPickerBottom: 60,
      emojiPickerMode: 'input' as 'input' | 'reaction',
      emojiPickerMessageId: null as number | null,
      emojiOptions: [
        '👍',
        '👎',
        '❤️',
        '😂',
        '😮',
        '🎉',
        '🔥',
        '👀',
        '✅',
        '❌',
        '💯',
        '🤔',
      ],
      /** File upload state */
      pendingAttachment: null as ChatAttachment | null,
      uploading: false,
      /** Authorized image-preview object URLs, keyed by blobName. */
      attachmentUrls: {} as Record<string, string>,
      attachmentRequests: markRaw(new Map<string, CancelTokenSource>()),
      attachmentErrors: {} as Record<string, string>,
      /** Lightbox state */
      lightboxOpen: false,
      lightboxSrc: '',
      lightboxAlt: '',
      lightboxBlobName: '',
      lightboxOriginalUrl: '',
      lightboxLoading: false,
      lightboxError: '',
      lightboxRequest: null as CancelTokenSource | null,
      supportVideoOpen: false,
      supportVideoTitle: '',
      supportVideoSourceUrl: '',
      supportVideoPlaybackRequest: null as
        | (() => Promise<SupportVideoPlayback>)
        | null,
      supportVideoMessageId: null as number | null,
      // Action currently being confirmed or canceled, so the buttons can be
      // disabled against a double click. The server is idempotent regardless.
      confirmingActionId: '',
      cancellingActionId: '',
      typedConfirmations: {} as Record<string, string>,
      actionErrors: {} as Record<string, string>,
      locallyExecutedActionIds: [] as string[],
      locallyCancelledActionIds: [] as string[],
      // What each opened card is currently showing, keyed "messageId:actionId".
      // Read from the server rather than from the AI's message, and replaced
      // whenever an edit is previewed or a confirmation comes back superseded.
      actionDetails: {} as Record<string, ProposedActionDetail>,
      // Edits the operator has made, as raw values keyed by field.
      actionEdits: {} as Record<string, Record<string, string>>,
      // Devices left ticked. Absent means all of them.
      actionSelectedUids: {} as Record<string, number[]>,
      loadingDetailKey: '',
    };
  },

  computed: {
    chatSuspended(): boolean {
      return this.$store.state.groupChat.chatSuspended;
    },
    currentUserId(): string {
      return this.$store.state.user?.id ?? '';
    },
    messages(): IChatMessage[] {
      return this.$store.state.groupChat.messages;
    },
    conversations(): GroupChatConversation[] {
      return this.$store.state.groupChat.conversations;
    },
    unreadConversationIds(): number[] {
      return this.$store.state.groupChat.unreadConversationIds;
    },
    participantCandidates(): GroupChatParticipant[] {
      return this.$store.state.groupChat.participantCandidates;
    },
    activeConversationId: {
      get(): number | null {
        return this.$store.state.groupChat.activeConversationId;
      },
      set(value: number | null) {
        if (value != null) {
          this.$store.dispatch('setActiveChatConversation', value).then(() => {
            this.previousMessageCount = this.messages.length;
            this.$nextTick(() => this.scrollToBottom());
          });
        }
      },
    },
    loading(): boolean {
      return this.$store.state.groupChat.loading;
    },
    loadingOlder(): boolean {
      return this.$store.state.groupChat.loadingOlder;
    },
    sending(): boolean {
      return this.$store.state.groupChat.sending;
    },
    hasMoreMessages(): boolean {
      return this.$store.state.groupChat.hasMoreMessages;
    },
    someoneTyping(): boolean {
      return this.$store.state.groupChat.someoneTyping;
    },
    propipeQueryPending(): boolean {
      return this.$store.state.groupChat.propipeQueryPending;
    },
    groupName(): string {
      return this.$store.state.groupChat.groupName;
    },
    chatTitle(): string {
      const name = this.groupName;
      if (!name) return 'Chat';
      const truncated = name.length > 25 ? name.substring(0, 25) + '…' : name;
      return `${truncated} Chat`;
    },
    conversationOptions(): Array<{ label: string; value: number }> {
      return this.conversations.map((conversation) => ({
        label: this.unreadConversationIds.includes(
          conversation.groupChatConversationId
        )
          ? `${this.getConversationLabel(conversation)} (new)`
          : this.getConversationLabel(conversation),
        value: conversation.groupChatConversationId,
      }));
    },
    activeConversation(): GroupChatConversation | null {
      return (
        this.conversations.find(
          (conversation) =>
            conversation.groupChatConversationId === this.activeConversationId
        ) ?? null
      );
    },
    canLeaveActiveConversation(): boolean {
      return (
        !!this.activeConversation &&
        this.activeConversation.groupChatConversationType !==
          GroupChatConversationType.PublicGroup
      );
    },
    leaveConversationLabel(): string {
      return this.activeConversation
        ? this.getConversationLabel(this.activeConversation)
        : 'this conversation';
    },
    participantOptions(): Array<{ label: string; value: string }> {
      return this.participantCandidates.map((participant) => ({
        label: participant.userName,
        value: participant.userId,
      }));
    },
    selectedParticipantDisplay(): string {
      if (this.selectedParticipantIds.length === 0) {
        return '';
      }

      const selectedNames = this.participantOptions
        .filter((option) => this.selectedParticipantIds.includes(option.value))
        .map((option) => option.label);

      if (selectedNames.length === 1) {
        return selectedNames[0];
      }

      return `${selectedNames.length} selected`;
    },
    charsRemaining(): number {
      return MAX_MESSAGE_LENGTH - this.draft.length;
    },
    isOverLimit(): boolean {
      return this.draft.length > MAX_MESSAGE_LENGTH;
    },
    expanded(): boolean {
      return this.viewState === 'expanded';
    },
    viewClass(): Record<string, boolean> {
      return {
        'group-chat--collapsed': this.viewState === 'collapsed',
        'group-chat--mini': this.viewState === 'mini',
        'group-chat--expanded': this.viewState === 'expanded',
        'group-chat--detached': this.detached,
        'group-chat--dragging': !!this.detachedDragStart,
      };
    },
    detachedStyle(): Record<string, string> {
      if (!this.detached) {
        return {};
      }

      const margin = DETACHED_VIEWPORT_MARGIN;
      const preferredSize = this.getDetachedPreferredSize();
      const displayPosition = this.clampDetachedPosition(
        this.detachedPosition.x,
        this.detachedPosition.y,
        preferredSize.width,
        preferredSize.height
      );
      const availableHeight = Math.max(
        42,
        window.innerHeight - displayPosition.y - margin
      );
      const style: Record<string, string> = {
        left: `${displayPosition.x}px`,
        width: `${preferredSize.width}px`,
      };

      if (this.viewState === 'collapsed') {
        style.bottom = `${margin}px`;
        style.maxHeight = `${availableHeight}px`;
        style.height = `${preferredSize.height}px`;
      } else if (this.viewState === 'mini') {
        style.bottom = `${margin}px`;
        style.height = `${preferredSize.height}px`;
      } else {
        style.top = `${displayPosition.y}px`;
        style.bottom = `${margin}px`;
      }

      return style;
    },
    headerIcon(): string {
      switch (this.viewState) {
        case 'collapsed':
          return 'expand_less';
        case 'mini':
          return 'open_in_full';
        case 'expanded':
          return 'close_fullscreen';
        default:
          return 'expand_less';
      }
    },
    atMenuOptions(): Array<{
      target: AiQueryTarget;
      mention: string;
      description: string;
    }> {
      return [
        {
          target: AiQueryTarget.Propipe,
          mention: AiQueryTargetToMentionMap[AiQueryTarget.Propipe],
          description: 'Ask about live operations or Propipe guidance',
        },
      ];
    },
  },

  watch: {
    lightboxOpen(open: boolean) {
      if (!open) this.releaseLightboxOriginal();
    },
    supportVideoOpen(open: boolean) {
      if (!open) this.resetSupportVideo();
    },
    currentUserId() {
      this.uiGeneration++;
      this.$store.commit('RESET_CHAT_ACCESS');
      this.draft = '';
      this.pendingAttachment = null;
      this.reportDialog =
        this.blockDialog =
        this.blockedUsersDialog =
        this.deleteDialog =
          false;
      this.reportTarget = null;
      this.blockTarget = null;
      this.reportNotes = '';
      this.blockedUsers = [];
      this.chatError = this.chatNotice = '';
      this.moderationBusy = this.blocksLoading = this.uploading = false;
      this.initialLoadComplete = false;
      this.updateVisibilityTimer();
      if (this.currentUserId) void this.loadChatSession();
    },
    chatSuspended(value: boolean) {
      if (value) this.pendingAttachment = null;
    },
    viewState(val: string) {
      this.updateVisibilityTimer();
      if (val === 'expanded') {
        this.unreadCount = 0;
        this.$nextTick(() => this.scrollToBottom());
      } else if (val === 'mini') {
        this.unreadCount = 0;
        this.$nextTick(() => this.scrollToBottom());
      }
      if (this.detached) {
        this.$nextTick(() => this.reclampDetachedPanel());
      }
      this.$emit('update:expanded', val === 'expanded' && !this.detached);
    },
    detached(val: boolean) {
      if (val) {
        this.$emit('update:expanded', false);
        this.$nextTick(() => this.reclampDetachedPanel());
      } else {
        this.$emit('update:expanded', this.viewState === 'expanded');
      }
    },
    // React to new messages arriving via the store (from SignalR or send).
    messages(newMessages: IChatMessage[]) {
      const blobs = new Set(
        newMessages.map((message) => message.attachment?.blobName)
      );
      for (const [blobName, request] of this.attachmentRequests) {
        if (!blobs.has(blobName)) request.cancel();
      }
      for (const blobName of Object.keys(this.attachmentErrors)) {
        if (!blobs.has(blobName)) delete this.attachmentErrors[blobName];
      }
      for (const [blobName, url] of Object.entries(this.attachmentUrls)) {
        if (!blobs.has(blobName)) {
          URL.revokeObjectURL(url);
          delete this.attachmentUrls[blobName];
        }
      }
      if (!blobs.has(this.lightboxBlobName)) {
        this.lightboxOpen = false;
        this.lightboxSrc = '';
      }
      if (
        this.supportVideoMessageId &&
        !newMessages.some(
          (message) => message.id === this.supportVideoMessageId
        )
      ) {
        this.supportVideoOpen = false;
        this.resetSupportVideo();
      }
      if (newMessages.length < this.previousMessageCount) this.unreadCount = 0;
      for (const key of Object.keys(this.actionDetails)) {
        if (!newMessages.some((message) => key.startsWith(`${message.id}:`))) {
          delete this.actionDetails[key];
          delete this.actionEdits[key];
          delete this.actionSelectedUids[key];
        }
      }
      if (newMessages.length > this.previousMessageCount) {
        // Only flash for incoming SignalR messages, not initial fetch or own sends.
        const newest = newMessages[newMessages.length - 1];
        if (
          this.initialLoadComplete &&
          newest &&
          newest.userId !== this.currentUserId
        ) {
          this.triggerNewMessageFlash();
        }
        if (this.viewState !== 'collapsed' && !this.userScrolledUp) {
          this.$nextTick(() => this.scrollToBottom());
        } else if (this.viewState === 'collapsed') {
          if (this.initialLoadComplete) {
            this.unreadCount += newMessages.length - this.previousMessageCount;
          }
        }
      }
      this.previousMessageCount = newMessages.length;
      this.loadImageAttachmentUrls(newMessages);
    },
    unreadConversationIds(newIds: number[], oldIds: number[]) {
      const addedCount = newIds.length - oldIds.length;
      if (!this.initialLoadComplete || addedCount <= 0) return;

      this.triggerNewMessageFlash();
      if (this.viewState === 'collapsed') {
        this.unreadCount += addedCount;
      }
    },
    someoneTyping(val: boolean) {
      if (val && this.viewState !== 'collapsed' && !this.userScrolledUp) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    propipeQueryPending(val: boolean) {
      if (val && this.viewState !== 'collapsed' && !this.userScrolledUp) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
    draft(val: string) {
      // Hide the Propipe menu when there is no active @ token near the cursor.
      if (this.showAtMenu && !this.hasActiveAtToken()) {
        this.showAtMenu = false;
      }
      if (!this.resolveAiQueryTarget(val)) {
        this.selectedAiQueryTarget = null;
      }
      if (val.length > 0 && !this.typingThrottled) {
        this.typingThrottled = true;
        this.$store.dispatch('sendChatTyping');
        setTimeout(() => {
          this.typingThrottled = false;
        }, 2000);
      }
    },
  },

  mounted() {
    void this.loadChatSession();
    window.addEventListener(
      'chat-visibility-changed',
      this.onRemoteVisibilityChanged
    );
    window.addEventListener('keydown', this.onWindowKeydown);
    window.addEventListener('resize', this.reclampDetachedPanel);
    document.addEventListener('visibilitychange', this.updateVisibilityTimer);
    this.updateVisibilityTimer();
    void this.reconcileVisibility();
  },

  beforeUnmount() {
    this.componentAlive = false;
    this.uiGeneration++;
    if (this.visibilityTimer) clearInterval(this.visibilityTimer);
    document.removeEventListener(
      'visibilitychange',
      this.updateVisibilityTimer
    );
    window.removeEventListener(
      'chat-visibility-changed',
      this.onRemoteVisibilityChanged
    );
    window.removeEventListener('keydown', this.onWindowKeydown);
    window.removeEventListener('resize', this.reclampDetachedPanel);
    window.removeEventListener('pointermove', this.onDetachedDragMove);
    window.removeEventListener('pointerup', this.endDetachedDrag);
    window.removeEventListener('pointercancel', this.endDetachedDrag);
    Object.values(this.attachmentUrls).forEach((url) =>
      URL.revokeObjectURL(url)
    );
    for (const request of this.attachmentRequests.values()) request.cancel();
    this.attachmentRequests.clear();
    this.releaseLightboxOriginal();
  },

  methods: {
    isCurrentChatSession(generation: number): boolean {
      return this.componentAlive && generation === this.uiGeneration;
    },

    async loadChatSession() {
      const generation = this.uiGeneration;
      await this.$store.dispatch('fetchChatConversations');
      if (!this.isCurrentChatSession(generation)) return;
      await this.$store.dispatch('fetchChatMessages');
      if (!this.isCurrentChatSession(generation)) return;
      this.previousMessageCount = this.messages.length;
      this.initialLoadComplete = true;
      this.$nextTick(() => this.scrollToBottom());
    },

    updateVisibilityTimer() {
      if (this.visibilityTimer) clearInterval(this.visibilityTimer);
      this.visibilityTimer = null;
      if (
        !this.currentUserId ||
        document.hidden ||
        this.viewState === 'collapsed'
      )
        return;
      void this.reconcileVisibility();
      this.visibilityTimer = setInterval(
        () => void this.reconcileVisibility(),
        15000
      );
    },

    async reconcileVisibility() {
      if (this.reconciling || !this.currentUserId || document.hidden) return;
      this.reconciling = true;
      const generation = this.uiGeneration;
      const wasSuspended = this.chatSuspended;
      try {
        await this.$store.dispatch('reconcileChatVisibility');
        if (!this.isCurrentChatSession(generation)) return;
        if (wasSuspended && !this.chatSuspended) await this.loadChatSession();
      } catch {
        if (!this.isCurrentChatSession(generation)) return;
        this.chatError =
          'Chat access could not be refreshed. Please try again.';
      } finally {
        this.reconciling = false;
      }
    },

    canModerateMessage(message: IChatMessage) {
      return (
        message.userId === this.currentUserId ||
        message.userId !== 'system|propipe-ai'
      );
    },

    openReport(message: IChatMessage) {
      if (message.userId === 'system|propipe-ai') return;
      this.reportTarget = message;
      this.reportReason = null;
      this.reportNotes = '';
      this.moderationError = '';
      this.reportDialog = true;
    },

    async submitReport() {
      if (
        !this.reportTarget ||
        this.reportTarget.userId === 'system|propipe-ai' ||
        !this.reportReason ||
        this.moderationBusy
      )
        return;
      this.moderationBusy = true;
      this.moderationError = '';
      const generation = this.uiGeneration;
      try {
        await GroupChatService.reportMessage(
          this.reportTarget.id,
          this.reportReason,
          this.reportNotes.trim()
        );
        if (!this.isCurrentChatSession(generation)) return;
        this.reportDialog = false;
        this.reportTarget = null;
        this.reportNotes = '';
        this.chatNotice = 'Report submitted.';
      } catch (error) {
        if (!this.isCurrentChatSession(generation)) return;
        this.moderationError = chatErrorMessage(
          error,
          'Report could not be submitted. Please try again.'
        );
      } finally {
        if (this.isCurrentChatSession(generation)) this.moderationBusy = false;
      }
    },

    openBlock(message: IChatMessage) {
      this.blockTarget = { userId: message.userId, userName: message.userName };
      this.moderationError = '';
      this.blockDialog = true;
    },

    onRemoteVisibilityChanged() {
      void this.refreshAfterBlock(true);
    },

    async refreshAfterBlock(refreshHistory = false) {
      const previousId = this.activeConversationId;
      const generation = this.uiGeneration;
      await this.reconcileVisibility();
      if (!this.isCurrentChatSession(generation) || this.chatSuspended) return;
      await this.$store.dispatch('fetchChatConversations');
      if (!this.isCurrentChatSession(generation)) return;
      await this.$store.dispatch('fetchChatParticipantCandidates');
      if (!this.isCurrentChatSession(generation)) return;
      if (previousId !== this.activeConversationId)
        await this.$store.dispatch('fetchChatMessages');
      else if (refreshHistory)
        await this.$store.dispatch('fetchChatMessages', true);
    },

    async confirmBlock() {
      if (!this.blockTarget || this.moderationBusy) return;
      this.moderationBusy = true;
      this.moderationError = '';
      const generation = this.uiGeneration;
      try {
        await this.$store.dispatch('setPersonalChatBlock', {
          userId: this.blockTarget.userId,
          blocked: true,
        });
        if (!this.isCurrentChatSession(generation)) return;
        this.blockDialog = false;
        this.chatNotice = 'User blocked.';
        await this.refreshAfterBlock();
      } catch (error) {
        if (!this.isCurrentChatSession(generation)) return;
        this.moderationError = chatErrorMessage(
          error,
          'User could not be blocked. Please try again.'
        );
      } finally {
        if (this.isCurrentChatSession(generation)) this.moderationBusy = false;
      }
    },

    openBlockedUsers() {
      this.blockedUsersDialog = true;
      void this.loadBlocks(0);
    },

    async loadBlocks(page: number) {
      if (this.blocksLoading) return;
      this.blocksLoading = true;
      this.blocksError = '';
      const userId = this.currentUserId;
      const generation = this.uiGeneration;
      try {
        const result = await GroupChatService.getBlocks(page);
        if (!this.componentAlive || userId !== this.currentUserId) return;
        this.blockedUsers = result.items;
        this.blockTotal = result.total;
        this.blockPage = page;
      } catch (error) {
        if (!this.isCurrentChatSession(generation)) return;
        this.blocksError = chatErrorMessage(
          error,
          'Blocked users could not be loaded.'
        );
      } finally {
        if (this.isCurrentChatSession(generation)) this.blocksLoading = false;
      }
    },

    async unblockUser(userId: string) {
      if (this.moderationBusy) return;
      this.moderationBusy = true;
      this.blocksError = '';
      const generation = this.uiGeneration;
      try {
        await this.$store.dispatch('setPersonalChatBlock', {
          userId,
          blocked: false,
        });
        if (!this.isCurrentChatSession(generation)) return;
        this.chatNotice =
          'Your block was removed. Chat may still be unavailable if the other person has blocked you.';
        await this.loadBlocks(
          this.blockedUsers.length === 1 && this.blockPage > 0
            ? this.blockPage - 1
            : this.blockPage
        );
        if (!this.isCurrentChatSession(generation)) return;
        await this.refreshAfterBlock(true);
      } catch (error) {
        if (!this.isCurrentChatSession(generation)) return;
        this.blocksError = chatErrorMessage(
          error,
          'User could not be unblocked. Please try again.'
        );
      } finally {
        if (this.isCurrentChatSession(generation)) this.moderationBusy = false;
      }
    },

    requestDelete(messageId: number) {
      this.deleteMessageId = messageId;
      this.moderationError = '';
      this.deleteDialog = true;
    },
    // ── UI handlers ────────────────────────────────────────────
    cycleState() {
      if (this.viewState === 'collapsed') {
        this.viewState = 'mini';
      } else if (this.viewState === 'mini') {
        this.viewState = 'expanded';
      } else {
        this.viewState = 'mini';
      }
    },

    toggleDetached() {
      if (this.detached) {
        this.detached = false;
        this.detachedDragStart = null;
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      const preferredSize = this.getDetachedPreferredSize();
      this.detachedPosition = this.clampDetachedPosition(
        rect?.left ?? 24,
        rect?.top ?? 96,
        preferredSize.width,
        preferredSize.height,
        this.viewState !== 'expanded'
      );
      this.detached = true;
      this.$nextTick(() => this.scrollToBottom());
    },

    getDetachedPreferredSize(): { width: number; height: number } {
      const margin = DETACHED_VIEWPORT_MARGIN;
      const maxWidth = window.innerWidth - margin * 2;
      const maxHeight = window.innerHeight - margin * 2;
      const width = Math.min(380, maxWidth);

      if (this.viewState === 'collapsed') {
        return { width, height: DETACHED_COLLAPSED_HEIGHT };
      }

      return {
        width,
        height:
          this.viewState === 'expanded'
            ? maxHeight
            : Math.min(
                Math.round(window.innerHeight * DETACHED_MINI_VIEWPORT_RATIO),
                maxHeight
              ),
      };
    },

    reclampDetachedPanel() {
      if (!this.detached) {
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      const preferredSize = this.getDetachedPreferredSize();
      this.detachedPosition = this.clampDetachedPosition(
        this.detachedPosition.x,
        this.detachedPosition.y,
        preferredSize.width,
        preferredSize.height,
        this.viewState !== 'expanded'
      );
    },

    startDetachedDrag(event: PointerEvent) {
      if (!this.detached || event.button !== 0) {
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      if (!rect) {
        return;
      }

      this.detachedDragStart = {
        pointerId: event.pointerId,
        pointerX: event.clientX,
        pointerY: event.clientY,
        panelX: rect.left,
        panelY: rect.top,
      };
      window.addEventListener('pointermove', this.onDetachedDragMove);
      window.addEventListener('pointerup', this.endDetachedDrag);
      window.addEventListener('pointercancel', this.endDetachedDrag);
      event.preventDefault();
    },

    onDetachedDragMove(event: PointerEvent) {
      const dragStart = this.detachedDragStart;
      if (!dragStart || event.pointerId !== dragStart.pointerId) {
        return;
      }

      const element = this.$el as HTMLElement | undefined;
      const rect = element?.getBoundingClientRect();
      const preferredSize = this.getDetachedPreferredSize();
      const width = preferredSize.width;
      const height = Math.max(preferredSize.height, rect?.height ?? 0);
      this.detachedPosition = this.clampDetachedPosition(
        dragStart.panelX + event.clientX - dragStart.pointerX,
        dragStart.panelY + event.clientY - dragStart.pointerY,
        width,
        height,
        this.viewState !== 'expanded'
      );
    },

    endDetachedDrag(event: PointerEvent) {
      if (
        this.detachedDragStart &&
        event.pointerId !== this.detachedDragStart.pointerId
      ) {
        return;
      }

      this.detachedDragStart = null;
      window.removeEventListener('pointermove', this.onDetachedDragMove);
      window.removeEventListener('pointerup', this.endDetachedDrag);
      window.removeEventListener('pointercancel', this.endDetachedDrag);
    },

    clampDetachedPosition(
      x: number,
      y: number,
      width: number,
      height: number,
      snapToBottom = false
    ) {
      const margin = DETACHED_VIEWPORT_MARGIN;
      const maxX = Math.max(margin, window.innerWidth - width - margin);
      const maxY = Math.max(margin, window.innerHeight - height - margin);
      return {
        x: Math.min(Math.max(x, margin), maxX),
        y: snapToBottom ? maxY : Math.min(Math.max(y, margin), maxY),
      };
    },

    getConversationLabel(conversation: GroupChatConversation): string {
      if (
        conversation.groupChatConversationType ===
        GroupChatConversationType.PublicGroup
      ) {
        return 'Public Group';
      }
      if (
        conversation.groupChatConversationType ===
        GroupChatConversationType.PropipeAi
      ) {
        return 'Propipe AI';
      }

      if (conversation.title) {
        return conversation.title;
      }

      const names = conversation.participants
        .filter((participant) => participant.userId !== this.currentUserId)
        .map((participant) => participant.userName)
        .filter(Boolean);
      if (names.length === 0) {
        return 'Private Chat';
      }
      if (names.length === 1) {
        return names[0];
      }

      return names.join(', ');
    },

    toggleNewConversation() {
      this.showNewConversation = !this.showNewConversation;
      if (this.showNewConversation) {
        this.$store.dispatch('fetchChatParticipantCandidates');
      }
    },

    createPrivateConversation() {
      if (this.selectedParticipantIds.length === 0) {
        return;
      }

      this.$store
        .dispatch('createChatConversation', {
          participantUserIds: this.selectedParticipantIds,
          isPropipeAi: false,
        })
        .then(() => {
          this.selectedParticipantIds = [];
          this.showNewConversation = false;
          this.previousMessageCount = this.messages.length;
        });
    },

    createPropipeAiConversation() {
      const existing = this.conversations.find(
        (conversation) =>
          conversation.groupChatConversationType ===
          GroupChatConversationType.PropipeAi
      );
      if (existing) {
        this.$store.dispatch(
          'setActiveChatConversation',
          existing.groupChatConversationId
        );
        return;
      }

      this.$store.dispatch('createChatConversation', {
        participantUserIds: [],
        isPropipeAi: true,
        title: 'Propipe AI',
      });
    },

    openLeaveConversationDialog() {
      if (!this.activeConversation || !this.canLeaveActiveConversation) {
        return;
      }

      this.leaveConfirmDialog = true;
    },

    confirmLeaveConversation() {
      if (!this.activeConversation || !this.canLeaveActiveConversation) {
        return;
      }

      this.$store.dispatch(
        'leaveChatConversation',
        this.activeConversation.groupChatConversationId
      );
    },

    formatTime(iso: string): string {
      // API timestamps without an offset are UTC, not browser-local time.
      const utcIso = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(iso) ? iso : `${iso}Z`;
      const d = new Date(utcIso);
      return (
        d.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: 'UTC',
        }) + ' UTC'
      );
    },

    scrollToBottom() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },

    onScroll() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      if (!el) return;

      // Trigger pagination when scrolled near the top.
      if (el.scrollTop < 50 && this.hasMoreMessages && !this.loadingOlder) {
        this.loadOlderMessages();
      }

      // Consider "at bottom" if within 40px of the end.
      this.userScrolledUp =
        el.scrollTop + el.clientHeight < el.scrollHeight - 40;
    },

    loadOlderMessages() {
      const el = this.$refs.messageList as HTMLElement | undefined;
      const previousScrollHeight = el?.scrollHeight ?? 0;

      this.$store.dispatch('loadOlderChatMessages').then(() => {
        // Preserve scroll position so older messages don't push the view down.
        this.$nextTick(() => {
          if (el) {
            el.scrollTop = el.scrollHeight - previousScrollHeight;
          }
        });
      });
    },

    async send() {
      const text = this.draft.trim();
      if (
        (!text && !this.pendingAttachment) ||
        this.isOverLimit ||
        this.sending ||
        this.uploading ||
        this.chatSuspended ||
        !this.activeConversationId
      )
        return;

      const attachment = this.pendingAttachment;
      const generation = this.uiGeneration;
      const aiQueryTarget: AiQueryTarget | null =
        this.selectedAiQueryTarget ?? this.resolveAiQueryTarget(text);
      this.chatError = this.chatNotice = '';
      try {
        await this.$store.dispatch('sendChatMessage', {
          text,
          attachment,
          aiQueryTarget,
        });
        if (!this.isCurrentChatSession(generation)) return;
        this.draft = '';
        this.pendingAttachment = null;
        this.selectedAiQueryTarget = null;
        this.$nextTick(() => this.scrollToBottom());
      } catch (error) {
        if (!this.isCurrentChatSession(generation)) return;
        this.chatError = chatErrorMessage(
          error,
          'Message could not be sent. Your draft has been kept.'
        );
        this.draft = text;
        this.pendingAttachment = attachment;
        void this.reconcileVisibility();
      }
    },

    async deleteMessage() {
      if (this.deleteMessageId == null || this.moderationBusy) return;
      this.moderationBusy = true;
      this.moderationError = '';
      const generation = this.uiGeneration;
      try {
        await this.$store.dispatch('deleteChatMessage', this.deleteMessageId);
        if (!this.isCurrentChatSession(generation)) return;
        this.deleteDialog = false;
      } catch (error) {
        if (!this.isCurrentChatSession(generation)) return;
        this.moderationError = chatErrorMessage(
          error,
          'Message could not be deleted. Please try again.'
        );
      } finally {
        if (this.isCurrentChatSession(generation)) this.moderationBusy = false;
      }
    },

    onInputKeydown(e: KeyboardEvent) {
      if (this.showAtMenu) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const optionCount = this.atMenuOptions.length;
          if (optionCount === 0) {
            return;
          }

          const direction = e.key === 'ArrowDown' ? 1 : -1;
          this.atMenuActiveIndex =
            (this.atMenuActiveIndex + direction + optionCount) % optionCount;
          return;
        }

        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          this.insertActiveAtMention();
          return;
        }

        if (e.key === 'Escape') {
          e.preventDefault();
          this.showAtMenu = false;
          return;
        }
      }

      // Show the Propipe menu whenever the user types '@'.
      if (e.key === '@') {
        this.showAtMenu = true;
        this.atMenuActiveIndex = 0;
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        this.send();
      }
    },

    insertActiveAtMention() {
      const option = this.atMenuOptions[this.atMenuActiveIndex];
      if (!option) {
        return;
      }

      this.insertMention(option.target);
    },

    insertMention(target: AiQueryTarget) {
      const input = this.getInputElement();
      const mention = AiQueryTargetToMentionMap[target];
      if (!input) {
        this.draft =
          this.draft.length === 0 ? `${mention} ` : `${this.draft} ${mention} `;
      } else {
        const cursorStart = input.selectionStart ?? this.draft.length;
        const cursorEnd = input.selectionEnd ?? this.draft.length;
        const tokenStart = this.findActiveAtTokenStart(cursorStart);
        const start = tokenStart >= 0 ? tokenStart : cursorStart;
        const end = cursorEnd;
        const token = `${mention} `;
        this.draft = `${this.draft.substring(
          0,
          start
        )}${token}${this.draft.substring(end)}`;
      }

      this.selectedAiQueryTarget = target;
      this.showAtMenu = false;
      this.$nextTick(() => {
        const el = this.getInputElement();
        if (el) {
          el.focus();
        }
      });
    },

    getInputElement(): HTMLInputElement | null {
      const inputRef = this.$refs.chatInput as any;
      const root = inputRef?.$el as HTMLElement | undefined;
      return (root?.querySelector('input') as HTMLInputElement | null) ?? null;
    },

    hasActiveAtToken(): boolean {
      const input = this.getInputElement();
      const cursor = input?.selectionStart ?? this.draft.length;
      const textBeforeCursor = this.draft.substring(0, cursor);
      const atIndex = textBeforeCursor.lastIndexOf('@');
      if (atIndex < 0) {
        return false;
      }

      const token = textBeforeCursor.substring(atIndex);
      return token.indexOf(' ') < 0;
    },

    findActiveAtTokenStart(cursor: number): number {
      const textBeforeCursor = this.draft.substring(0, cursor);
      const atIndex = textBeforeCursor.lastIndexOf('@');
      if (atIndex < 0) {
        return -1;
      }

      // Ensure the @ token starts at the beginning or after whitespace.
      if (atIndex > 0 && !/\s/.test(textBeforeCursor[atIndex - 1])) {
        return -1;
      }

      // If whitespace exists after @ before cursor, this is not an active token.
      const token = textBeforeCursor.substring(atIndex);
      if (/\s/.test(token)) {
        return -1;
      }

      return atIndex;
    },

    resolveAiQueryTarget(text: string): AiQueryTarget | null {
      return resolveAiQueryTargetFromText(text);
    },

    tokenizeMessageMentions(text: string): IMessageSegment[] {
      if (!text) {
        return [];
      }

      const matches: Array<{
        start: number;
        end: number;
        target: AiQueryTarget;
      }> = [];

      for (const [mention, target] of Object.entries(AiQueryTargetMentionMap)) {
        const escapedMention = mention.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(^|\\s)(${escapedMention})\\b`, 'gi');
        let match: RegExpExecArray | null;

        while ((match = regex.exec(text)) !== null) {
          const leadingWhitespace = match[1] ?? '';
          const mentionText = match[2];
          const start = match.index + leadingWhitespace.length;
          const end = start + mentionText.length;
          matches.push({ start, end, target });

          if (mentionText.length === 0) {
            break;
          }
        }
      }

      if (matches.length === 0) {
        return [{ text, isMention: false, target: null }];
      }

      matches.sort((a, b) => a.start - b.start || b.end - a.end);

      const segments: IMessageSegment[] = [];
      let cursor = 0;

      for (const match of matches) {
        if (match.start < cursor) {
          continue;
        }

        if (match.start > cursor) {
          segments.push({
            text: text.substring(cursor, match.start),
            isMention: false,
            target: null,
          });
        }

        segments.push({
          text: text.substring(match.start, match.end),
          isMention: true,
          target: match.target,
        });

        cursor = match.end;
      }

      if (cursor < text.length) {
        segments.push({
          text: text.substring(cursor),
          isMention: false,
          target: null,
        });
      }

      return segments;
    },

    mentionClass(target: AiQueryTarget | null): string {
      switch (target) {
        case AiQueryTarget.Propipe:
          return 'group-chat__mention--propipe';
        default:
          return '';
      }
    },

    getReactions(messageId: number) {
      return this.$store.state.groupChat.reactions[messageId] || [];
    },

    toggleReaction(messageId: number, emoji: string) {
      this.$store.dispatch('toggleReaction', { messageId, emoji });
    },

    openEmojiPicker(event: MouseEvent, messageId: number) {
      this.emojiPickerMode = 'reaction';
      this.emojiPickerMessageId = messageId;

      const chatEl = this.$el as HTMLElement;
      const chatRect = chatEl.getBoundingClientRect();
      const bottomOffset = chatRect.bottom - event.clientY;

      // Clamp so the picker (~120px tall) stays within the chat bounds.
      const pickerHeight = 120;
      this.emojiPickerBottom = Math.max(
        8,
        Math.min(bottomOffset, chatRect.height - pickerHeight - 8)
      );

      this.emojiPickerOpen = true;
    },

    openInputEmojiPicker() {
      this.emojiPickerMode = 'input';
      this.emojiPickerMessageId = null;
      this.emojiPickerBottom = 50;
      this.emojiPickerOpen = true;
    },

    onEmojiSelected(emoji: string) {
      if (
        this.emojiPickerMode === 'reaction' &&
        this.emojiPickerMessageId != null
      ) {
        const existing = this.getReactions(this.emojiPickerMessageId).find(
          (r: any) => r.emoji === emoji
        );
        const alreadyReacted = existing?.userIds?.includes(this.currentUserId);

        // Picker is add-only to avoid accidental removals.
        // Users can still toggle off by clicking the reaction chip itself.
        if (!alreadyReacted) {
          this.toggleReaction(this.emojiPickerMessageId, emoji);
        }
      } else {
        this.draft += emoji;
      }
      this.emojiPickerOpen = false;
    },

    triggerFileInput() {
      (this.$refs.fileInput as HTMLInputElement)?.click();
    },

    async onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input?.files?.[0];
      if (!file) return;
      input.value = ''; // Reset so the same file can be selected again

      await this.uploadFile(file);
    },

    async onPaste(event: ClipboardEvent) {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (!file) break;
          await this.uploadFile(file);
          break;
        }
      }
    },

    async uploadFile(file: File) {
      if (this.uploading || this.sending || this.chatSuspended) return;
      const isImage = /\.(jpe?g|png)$/i.test(file.name);
      const isDocument =
        /\.(pdf|docx|xlsx|pptx|csv|agm|onl|onlv|agmv|ofl|zip|text|txt|md|json|xml)$/i.test(
          file.name
        );
      if (
        (!isImage && !isDocument) ||
        (isImage &&
          file.type &&
          !['image/jpeg', 'image/png'].includes(file.type)) ||
        (isDocument && file.type.startsWith('image/')) ||
        file.size === 0 ||
        file.size > (isImage ? 4 : 10) * 1024 * 1024
      ) {
        this.chatError =
          'Choose a still JPEG/PNG up to 4 MB or a supported document up to 10 MB.';
        return;
      }

      this.uploading = true;
      this.chatError = this.chatNotice = '';
      const userId = this.currentUserId;
      try {
        const attachment = await GroupChatService.uploadFile(file);
        if (
          this.componentAlive &&
          userId === this.currentUserId &&
          !this.chatSuspended
        )
          this.pendingAttachment = attachment;
      } catch (error) {
        this.chatError = chatErrorMessage(
          error,
          'Attachment could not be uploaded. Please try again.'
        );
      } finally {
        this.uploading = false;
      }
    },

    async openImagePreview(blobName: string, originalName: string) {
      const src = this.attachmentUrls[blobName];
      if (!src) return;
      this.releaseLightboxOriginal();
      this.lightboxSrc = src;
      this.lightboxAlt = originalName;
      this.lightboxBlobName = blobName;
      this.lightboxOpen = true;
      this.lightboxLoading = true;
      const request = markRaw(axios.CancelToken.source());
      this.lightboxRequest = request;
      const generation = this.uiGeneration;
      try {
        const url = await GroupChatService.getAttachmentUrl(
          blobName,
          false,
          request.token
        );
        if (
          !this.isCurrentChatSession(generation) ||
          this.lightboxRequest !== request ||
          !this.lightboxOpen ||
          !this.messages.some(
            (message) => message.attachment?.blobName === blobName
          )
        ) {
          URL.revokeObjectURL(url);
          return;
        }
        this.lightboxOriginalUrl = url;
        this.lightboxSrc = url;
      } catch (error) {
        if (
          !axios.isCancel(error) &&
          this.isCurrentChatSession(generation) &&
          this.lightboxRequest === request
        ) {
          this.lightboxError = chatErrorMessage(
            error,
            'The full-resolution image could not be loaded.'
          );
        }
      } finally {
        if (this.lightboxRequest === request) {
          this.lightboxLoading = false;
          this.lightboxRequest = null;
        }
      }
    },

    releaseLightboxOriginal() {
      this.lightboxRequest?.cancel();
      this.lightboxRequest = null;
      if (this.lightboxOriginalUrl)
        URL.revokeObjectURL(this.lightboxOriginalUrl);
      this.lightboxOriginalUrl = '';
      this.lightboxLoading = false;
      this.lightboxError = '';
    },

    onWindowKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape' && this.lightboxOpen) {
        this.lightboxOpen = false;
      }
    },

    async downloadAttachment(blobName: string, originalName: string) {
      const userId = this.currentUserId;
      try {
        const url = await GroupChatService.getAttachmentUrl(blobName);
        try {
          if (
            !this.componentAlive ||
            userId !== this.currentUserId ||
            this.chatSuspended ||
            !this.messages.some(
              (message: IChatMessage) =>
                message.attachment?.blobName === blobName
            )
          )
            return;
          const link = document.createElement('a');
          link.href = url;
          link.download = originalName;
          link.click();
        } finally {
          URL.revokeObjectURL(url);
        }
      } catch (e) {
        this.chatError =
          'Attachment could not be downloaded. Please try again.';
      }
    },

    formatCitationLabel(citation: SupportCitation): string {
      if (citation.pageNumber != null) {
        return `${citation.title}, p. ${citation.pageNumber}`;
      }
      return citation.section
        ? `${citation.title}, ${citation.section}`
        : citation.title;
    },

    userHasAnyRole(...roles: string[]): boolean {
      return hasAnyRole(this.$store.state.user, ...roles);
    },

    /**
     * Returns the proposed commands to show on an AI answer.
     *
     * Only the person who asked sees a card, and only when they hold an
     * administrator role. This is presentation only: the server applies the
     * same conditions and refuses anything else, so nothing here is a
     * security control.
     */
    visibleProposals(msg: IChatMessage): ProposedDeviceCommand[] {
      if (
        msg.userId !== 'system|propipe-ai' ||
        !msg.proposedCommands ||
        msg.proposedCommands.length === 0
      ) {
        return [];
      }
      if (!this.userHasAnyRole('admin', 'superadmin')) {
        return [];
      }
      return msg.proposedCommands;
    },

    isProposalExecuted(msg: IChatMessage, proposal: ProposedDeviceCommand) {
      const key = `${msg.id}:${proposal.actionId}`;
      return (
        this.locallyExecutedActionIds.includes(key) ||
        (msg.executedActionIds ?? []).includes(proposal.actionId)
      );
    },

    isProposalCancelled(msg: IChatMessage, proposal: ProposedDeviceCommand) {
      const key = `${msg.id}:${proposal.actionId}`;
      return (
        this.locallyCancelledActionIds.includes(key) ||
        (msg.cancelledActionIds ?? []).includes(proposal.actionId)
      );
    },

    isProposalExpired(proposal: ProposedDeviceCommand): boolean {
      return isProposedCommandExpired(proposal);
    },

    describeProposalTransport(proposal: ProposedDeviceCommand): string {
      return describeTransport(proposal);
    },

    actionKey(msg: IChatMessage, proposal: ProposedDeviceCommand): string {
      return `${msg.id}:${proposal.actionId}`;
    },

    proposalDetail(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand
    ): ProposedActionDetail | null {
      return this.actionDetails[this.actionKey(msg, proposal)] ?? null;
    },

    isProposalBusy(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand
    ): boolean {
      const key = this.actionKey(msg, proposal);
      return (
        this.loadingDetailKey === key ||
        this.confirmingActionId === proposal.actionId ||
        this.cancellingActionId === proposal.actionId
      );
    },

    /**
     * Reads what the command would actually write.
     *
     * This is a separate call rather than part of the message because an
     * aggregate command writes settings nobody asked about, and those settings
     * move. Rendering them from the AI's message would state the device is as
     * it was when the answer was written.
     */
    async openProposal(msg: IChatMessage, proposal: ProposedDeviceCommand) {
      const key = this.actionKey(msg, proposal);
      this.loadingDetailKey = key;
      this.actionErrors = { ...this.actionErrors, [key]: '' };
      try {
        const detail = await GroupChatService.getProposedActionDetail(
          msg.id,
          proposal.actionId
        );
        this.applyDetail(key, detail);
      } catch (e: any) {
        this.actionErrors = {
          ...this.actionErrors,
          [key]: this.readActionError(
            e,
            'The command details could not be read. Please try again.'
          ),
        };
      } finally {
        this.loadingDetailKey = '';
      }
    },

    applyDetail(key: string, detail: ProposedActionDetail) {
      this.actionDetails = { ...this.actionDetails, [key]: detail };
      const edits: Record<string, string> = {};
      detail.fields.forEach((field) => {
        edits[field.fieldKey] = field.newRaw;
      });
      this.actionEdits = { ...this.actionEdits, [key]: edits };
      if (!this.actionSelectedUids[key]) {
        this.actionSelectedUids = {
          ...this.actionSelectedUids,
          [key]: detail.targets.map((target) => target.uid),
        };
      }
    },

    fieldValue(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand,
      field: ProposedActionField
    ): string {
      const edits = this.actionEdits[this.actionKey(msg, proposal)] ?? {};
      return edits[field.fieldKey] ?? field.newRaw;
    },

    editField(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand,
      field: ProposedActionField,
      value: unknown
    ) {
      const key = this.actionKey(msg, proposal);
      const raw =
        value === null || value === undefined ? '' : String(value).trim();
      this.actionEdits = {
        ...this.actionEdits,
        [key]: { ...(this.actionEdits[key] ?? {}), [field.fieldKey]: raw },
      };
      this.refreshProposal(msg, proposal);
    },

    selectedUids(msg: IChatMessage, proposal: ProposedDeviceCommand): number[] {
      return this.actionSelectedUids[this.actionKey(msg, proposal)] ?? [];
    },

    isTargetSelected(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand,
      uid: number
    ): boolean {
      return this.selectedUids(msg, proposal).includes(uid);
    },

    isOnlySelectedTarget(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand,
      uid: number
    ): boolean {
      const selected = this.selectedUids(msg, proposal);
      return selected.length === 1 && selected[0] === uid;
    },

    toggleTarget(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand,
      uid: number
    ) {
      const key = this.actionKey(msg, proposal);
      const selected = this.selectedUids(msg, proposal);
      const next = selected.includes(uid)
        ? selected.filter((value) => value !== uid)
        : [...selected, uid];
      if (next.length === 0) {
        return;
      }
      this.actionSelectedUids = { ...this.actionSelectedUids, [key]: next };
      this.refreshProposal(msg, proposal);
    },

    /**
     * Re-reads the card with the current edits applied.
     *
     * An edit changes what the command writes, so it changes the fingerprint.
     * Reading it back is what makes the operator confirm the settings their
     * edit produced rather than the ones they started from.
     */
    async refreshProposal(msg: IChatMessage, proposal: ProposedDeviceCommand) {
      const key = this.actionKey(msg, proposal);
      const detail = this.actionDetails[key];
      if (!detail) {
        return;
      }
      this.loadingDetailKey = key;
      this.actionErrors = { ...this.actionErrors, [key]: '' };
      try {
        const refreshed = await GroupChatService.previewProposedAction(
          msg.id,
          proposal.actionId,
          {
            amendments: collectAmendments(
              detail.fields,
              this.actionEdits[key] ?? {}
            ),
            uids: this.selectedUids(msg, proposal),
          }
        );
        this.applyDetail(key, refreshed);
      } catch (e: any) {
        this.actionErrors = {
          ...this.actionErrors,
          [key]: this.readActionError(
            e,
            'That change could not be applied. Please try again.'
          ),
        };
      } finally {
        this.loadingDetailKey = '';
      }
    },

    /**
     * Whether the Send button should be live.
     *
     * A destructive command requires the operator to type SEND. Restarting a
     * unit in the field is not something a stray click should be able to do.
     */
    canSendProposal(
      msg: IChatMessage,
      proposal: ProposedDeviceCommand
    ): boolean {
      const key = this.actionKey(msg, proposal);
      const detail = this.actionDetails[key];
      if (
        !detail ||
        this.isProposalBusy(msg, proposal) ||
        this.isProposalExecuted(msg, proposal) ||
        this.isProposalCancelled(msg, proposal) ||
        this.isProposalExpired(proposal)
      ) {
        return false;
      }
      if (!detail.requiresTypedConfirmation) {
        return true;
      }
      return (
        (this.typedConfirmations[key] ?? '').trim().toUpperCase() === 'SEND'
      );
    },

    /**
     * Collapses the opened settings back to the card header. The proposal stays
     * live and can be reviewed again; Cancel is what abandons it.
     */
    dismissProposal(msg: IChatMessage, proposal: ProposedDeviceCommand) {
      const key = this.actionKey(msg, proposal);
      const { [key]: _detail, ...remainingDetails } = this.actionDetails;
      const { [key]: _edits, ...remainingEdits } = this.actionEdits;
      const { [key]: _uids, ...remainingUids } = this.actionSelectedUids;
      const { [key]: _typed, ...remainingTyped } = this.typedConfirmations;
      const { [key]: _error, ...remainingErrors } = this.actionErrors;
      this.actionDetails = remainingDetails;
      this.actionEdits = remainingEdits;
      this.actionSelectedUids = remainingUids;
      this.typedConfirmations = remainingTyped;
      this.actionErrors = remainingErrors;
    },

    /**
     * Reads the server's reason for a refusal. Showing it on the card is what
     * tells the operator whether to ask Propipe again or to stop.
     */
    readActionError(error: any, fallback: string): string {
      const data = error?.response?.data;
      if (typeof data === 'string' && data.length > 0) {
        return data;
      }
      if (typeof data?.message === 'string' && data.message.length > 0) {
        return data.message;
      }
      return fallback;
    },

    async cancelProposal(msg: IChatMessage, proposal: ProposedDeviceCommand) {
      const key = this.actionKey(msg, proposal);
      this.cancellingActionId = proposal.actionId;
      this.actionErrors = { ...this.actionErrors, [key]: '' };
      try {
        const result = await GroupChatService.cancelProposedAction(
          msg.id,
          proposal.actionId
        );
        this.locallyCancelledActionIds.push(key);
        this.dismissProposal(msg, proposal);
        this.$q.notify({
          type: 'info',
          message: result.alreadyCancelled
            ? 'This command had already been canceled.'
            : 'Command canceled.',
        });
      } catch (e: any) {
        this.actionErrors = {
          ...this.actionErrors,
          [key]: this.readActionError(
            e,
            'The command could not be canceled. Please try again.'
          ),
        };
      } finally {
        this.cancellingActionId = '';
      }
    },

    async confirmProposal(msg: IChatMessage, proposal: ProposedDeviceCommand) {
      const key = this.actionKey(msg, proposal);
      const detail = this.actionDetails[key];
      if (!detail) {
        return;
      }
      this.confirmingActionId = proposal.actionId;
      this.actionErrors = { ...this.actionErrors, [key]: '' };
      try {
        const result = await GroupChatService.executeProposedAction(
          msg.id,
          proposal.actionId,
          {
            fingerprint: detail.fingerprint,
            amendments: collectAmendments(
              detail.fields,
              this.actionEdits[key] ?? {}
            ),
            uids: this.selectedUids(msg, proposal),
          }
        );
        this.locallyExecutedActionIds.push(key);
        this.$q.notify({
          type: 'positive',
          message: result.alreadyExecuted
            ? 'This command had already been sent.'
            : `Queued ${result.commandQueueIds.length} command(s).`,
        });
      } catch (e: any) {
        // A refusal because the devices moved carries the re-read card with it.
        // Swapping it in is what lets the operator see what changed and decide
        // again, rather than confirming settings that are no longer true.
        const refreshed = e?.response?.data?.detail;
        if (refreshed && typeof refreshed.fingerprint === 'string') {
          this.applyDetail(key, refreshed);
        }
        this.actionErrors = {
          ...this.actionErrors,
          [key]: this.readActionError(
            e,
            'The command could not be sent. Please try again.'
          ),
        };
      } finally {
        this.confirmingActionId = '';
      }
    },

    async openSupportSource(messageId: number, citation: SupportCitation) {
      if (isPrivateVideoCitation(citation)) {
        this.supportVideoTitle = citation.title;
        this.supportVideoSourceUrl = '';
        this.supportVideoMessageId = messageId;
        this.supportVideoPlaybackRequest = () =>
          GroupChatService.getSupportVideoPlayback(
            messageId,
            citation.documentId
          );
        this.supportVideoOpen = true;
        return;
      }

      const videoUrl = getYouTubeEmbedUrl(citation.sourceUrl);
      if (videoUrl) {
        this.supportVideoTitle = citation.title;
        this.supportVideoSourceUrl = citation.sourceUrl ?? '';
        this.supportVideoPlaybackRequest = null;
        this.supportVideoMessageId = messageId;
        this.supportVideoOpen = true;
        return;
      }

      if (citation.sourceUrl) {
        window.open(citation.sourceUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      const generation = this.uiGeneration;
      await openSupportDocument(
        citation.title,
        () =>
          GroupChatService.getSupportSourceUrl(messageId, citation.documentId),
        (message) => this.$q.notify({ type: 'negative', message }),
        () =>
          this.isCurrentChatSession(generation) &&
          this.messages.some((message) => message.id === messageId)
      );
    },

    resetSupportVideo() {
      this.supportVideoSourceUrl = '';
      this.supportVideoPlaybackRequest = null;
      this.supportVideoMessageId = null;
    },

    async downloadLightboxImage() {
      try {
        const response = await fetch(this.lightboxOriginalUrl);
        const blob = await response.blob();
        const ext = this.lightboxAlt.includes('.')
          ? this.lightboxAlt.split('.').pop()
          : blob.type.split('/')[1] || 'png';
        const timestamp = new Date()
          .toISOString()
          .replace(/[:.]/g, '-')
          .replace('T', '_')
          .slice(0, 19);
        const filename = `${timestamp}-propipe-chat.${ext}`;
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(objectUrl);
      } catch (e) {
        console.error('Failed to download image:', e);
      }
    },

    formatFileSize(bytes: number): string {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },

    loadImageAttachmentUrls(msgs: IChatMessage[]) {
      for (const msg of msgs) {
        if (this.attachmentRequests.size >= 3) break;
        if (
          msg.attachment &&
          msg.attachment.contentType?.startsWith('image/') &&
          msg.attachment.blobName &&
          !this.attachmentUrls[msg.attachment.blobName] &&
          !this.attachmentRequests.has(msg.attachment.blobName) &&
          !this.attachmentErrors[msg.attachment.blobName]
        ) {
          const request = markRaw(axios.CancelToken.source());
          this.attachmentRequests.set(msg.attachment.blobName, request);
          void this.loadImageAttachmentUrl(msg.attachment.blobName, request);
        }
      }
    },

    async loadImageAttachmentUrl(blobName: string, request: CancelTokenSource) {
      const generation = this.uiGeneration;
      try {
        const url = await GroupChatService.getAttachmentUrl(
          blobName,
          true,
          request.token
        );
        if (
          !this.isCurrentChatSession(generation) ||
          !this.messages.some(
            (message) => message.attachment?.blobName === blobName
          )
        ) {
          URL.revokeObjectURL(url);
          return;
        }
        this.attachmentUrls = { ...this.attachmentUrls, [blobName]: url };
        this.$nextTick(() => {
          if (!this.userScrolledUp) this.scrollToBottom();
        });
      } catch (error) {
        if (
          !axios.isCancel(error) &&
          this.isCurrentChatSession(generation) &&
          this.messages.some(
            (message) => message.attachment?.blobName === blobName
          )
        ) {
          this.attachmentErrors = {
            ...this.attachmentErrors,
            [blobName]: chatErrorMessage(
              error,
              'The image could not be loaded.'
            ),
          };
        }
      } finally {
        if (this.attachmentRequests.get(blobName) === request)
          this.attachmentRequests.delete(blobName);
        if (this.componentAlive) this.loadImageAttachmentUrls(this.messages);
      }
    },

    retryImageAttachment(blobName: string) {
      delete this.attachmentErrors[blobName];
      this.loadImageAttachmentUrls(this.messages);
    },

    renderMarkdown(text: string): string {
      // Sanitize: strip HTML tags from the AI response before parsing markdown.
      // The AI should never produce HTML, but this prevents any injected tags
      // from rendering via v-html.
      const sanitized = text
        .replace(/\[ref_id:[^\]]+\]/g, '')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      return marked.parse(sanitized) as string;
    },

    triggerNewMessageFlash() {
      if (this.newMessageFlashTimer) clearTimeout(this.newMessageFlashTimer);
      this.newMessageFlash = false;
      // Force a reflow so the animation restarts if already playing.
      void (this.$el as HTMLElement)?.offsetWidth;
      this.newMessageFlash = true;
      this.newMessageFlashTimer = setTimeout(() => {
        this.newMessageFlash = false;
        this.newMessageFlashTimer = null;
      }, 2000);
    },
  },
});
</script>

<style scoped>
.group-chat {
  display: flex;
  flex-direction: column;
  border-top: 1px solid #ddd;
  background: #fff;
  margin-top: auto;
  flex-shrink: 0;
  box-sizing: border-box;
}

.group-chat--detached {
  position: fixed;
  z-index: 4000;
  margin-top: 0;
  box-sizing: border-box;
  border: 1px solid #c8ccd6;
  border-radius: 8px;
  box-shadow: 0 16px 42px rgba(25, 35, 65, 0.28);
  overflow: hidden;
}

.group-chat--detached.group-chat--mini {
  height: auto;
}

.group-chat--detached.group-chat--expanded {
  flex: none;
}

.group-chat--detached.group-chat--collapsed {
  height: auto;
}

.group-chat--dragging {
  user-select: none;
}

.group-chat__title--flash {
  animation: chat-title-flash 2s ease-out forwards;
}

@keyframes chat-title-flash {
  0% {
    color: #ff0080;
    text-shadow: 0 0 6px rgba(255, 0, 128, 0.7);
  }
  16% {
    color: #ff6600;
    text-shadow: 0 0 6px rgba(255, 102, 0, 0.7);
  }
  33% {
    color: #f5d800;
    text-shadow: 0 0 6px rgba(245, 216, 0, 0.7);
  }
  50% {
    color: #00cc55;
    text-shadow: 0 0 6px rgba(0, 204, 85, 0.7);
  }
  66% {
    color: #0099ff;
    text-shadow: 0 0 6px rgba(0, 153, 255, 0.7);
  }
  83% {
    color: #9900ff;
    text-shadow: 0 0 6px rgba(153, 0, 255, 0.7);
  }
  100% {
    color: #fff;
    text-shadow: none;
  }
}

/* Collapsed — header bar only */
.group-chat--collapsed {
  height: auto;
}

/* Mini — fixed compact height */
.group-chat--mini {
  height: 50vh;
}

/* Expanded — take all remaining space in the drawer */
.group-chat--expanded {
  height: auto;
  flex: 1 1 0;
  min-height: 0;
}

.group-chat__header {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #24337a;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
  user-select: none;
  min-height: 42px;
  box-sizing: border-box;
}

.group-chat--detached .group-chat__header {
  cursor: move;
}

.group-chat__title {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.3px;
}

/* ── Message list ─────────────────────────────────── */
.group-chat__messages {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  background: #f5f5f5;
  -webkit-overflow-scrolling: touch;
}

/* ── Bubble ───────────────────────────────────────── */
.group-chat__bubble {
  max-width: 85%;
  padding: 6px 10px;
  border-radius: 10px;
  background: #e3e8f0;
  align-self: flex-start;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.group-chat__bubble--own {
  background: #d1e7dd;
  align-self: flex-end;
}

.group-chat__bubble--event {
  max-width: 92%;
  align-self: center;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 9px;
  border-radius: 999px;
  background: #eef1f5;
  color: #5f6673;
  font-size: 11px;
}

.group-chat__event-time {
  color: #888;
  white-space: nowrap;
}

.group-chat__meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 2px;
}

.group-chat__author {
  font-size: 11px;
  font-weight: 600;
  color: #24337a;
}

.group-chat__bubble--own .group-chat__author {
  color: #0f5132;
}

.group-chat__time {
  font-size: 10px;
  color: #888;
}

.group-chat__text {
  font-size: 13px;
  line-height: 1.35;
  color: #222;
}

/* ── Input area ───────────────────────────────────── */
.group-chat__input {
  position: relative;
  padding: 6px 8px;
  border-top: 1px solid #ddd;
  background: #fff;
  flex-shrink: 0;
}

.group-chat__feedback {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-size: 12px;
  line-height: 1.35;
}

.group-chat__feedback > span {
  flex: 1;
  min-width: 0;
}

.group-chat__field {
  font-size: 13px;
}

.group-chat__loading {
  text-align: center;
  font-size: 11px;
  color: #999;
  padding: 6px 0;
}

.group-chat__delete {
  opacity: 0;
  transition: opacity 0.15s;
  margin-left: auto;
}

.group-chat__bubble:hover .group-chat__delete {
  opacity: 1;
}

.group-chat__char-count {
  font-size: 11px;
  color: #999;
  margin-right: 4px;
  line-height: 1;
}

.group-chat__char-count--over {
  color: #c10015;
  font-weight: 600;
}

/* ── Viewer notice banner ─────────────────────────── */
.group-chat__banner {
  padding: 6px 12px;
  background: #ff9800;
  color: #fff;
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  flex-shrink: 0;
}

.group-chat__conversations {
  padding: 6px 8px;
  border-bottom: 1px solid #ddd;
  background: #fff;
  flex-shrink: 0;
}

.group-chat__conversation-select {
  font-size: 12px;
}

.group-chat__conversation-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
}

.group-chat__new-chat {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
}

.group-chat__new-chat .q-btn {
  flex: 0 0 auto;
}

.group-chat__participant-select {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
}

.group-chat__participant-select ::v-deep(.q-field__native),
.group-chat__participant-select ::v-deep(.q-field__input) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Empty state ──────────────────────────────────── */
.group-chat__empty {
  text-align: center;
  font-size: 12px;
  color: #999;
  padding: 24px 10px;
  font-style: italic;
}

/* ── Typing indicator ─────────────────────────────── */
.group-chat__typing {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: #666;
  font-style: italic;
}

.typing-dots {
  display: inline-flex;
  gap: 3px;
}

.typing-dots span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #888;
  animation: typing-bounce 1.4s ease-in-out infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-bounce {
  0%,
  60%,
  100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
}

/* ── Bot / Propipe styles ─────────────────────────── */
.group-chat__bubble--bot {
  background: #ede7f6;
  border-left: 3px solid #7c4dff;
}

.group-chat__author--bot {
  color: #7c4dff;
  display: flex;
  align-items: center;
}

.group-chat__bot-icon {
  width: 14px;
  height: 14px;
  margin-right: 4px;
  filter: brightness(0) saturate(100%) invert(22%) sepia(80%) saturate(4000%)
    hue-rotate(258deg) brightness(92%) contrast(100%);
}

.group-chat__sources {
  border-top: 1px solid rgba(72, 54, 106, 0.18);
  display: flex;
  flex-wrap: wrap;
  gap: 2px 6px;
  margin-top: 8px;
  padding-top: 6px;
}

.group-chat__sources-label {
  align-self: center;
  color: #4f455e;
  font-size: 11px;
  font-weight: 700;
  margin-right: 2px;
}

/* ── Proposed device command card ─────────────────── */
.group-chat__action {
  background: #fbf9ff;
  border: 1px solid rgba(72, 54, 106, 0.28);
  border-radius: 6px;
  box-sizing: border-box;
  margin-top: 8px;
  max-width: 100%;
  min-width: 0;
  padding: 8px 10px;
}

.group-chat__action-header {
  align-items: center;
  color: #4f455e;
  display: flex;
  font-size: 11px;
  font-weight: 700;
  gap: 6px;
}

.group-chat__action-title {
  flex: 1;
}

.group-chat__action-summary {
  color: #2c2c2c;
  font-size: 12px;
  margin-top: 6px;
}

.group-chat__action-targets {
  color: #4f455e;
  font-size: 11px;
  margin-top: 4px;
  word-break: break-word;
}

.group-chat__action-state {
  align-items: center;
  display: flex;
  font-size: 11px;
  gap: 4px;
  margin-top: 6px;
}

.group-chat__action-state--sent {
  color: #21ba45;
}

.group-chat__action-state--cancelled {
  color: #8d6e63;
}

.group-chat__action-state--expired {
  color: #8d6e63;
}

.group-chat__action-warning {
  align-items: center;
  color: #c10015;
  display: flex;
  font-size: 11px;
  font-weight: 600;
  gap: 4px;
  margin-top: 6px;
  min-width: 0;
  overflow-wrap: anywhere;
}

.group-chat__action-warning :deep(.q-icon) {
  flex: 0 0 auto;
}

.group-chat__action-section {
  border-top: 1px solid rgba(72, 54, 106, 0.16);
  color: #4f455e;
  font-size: 11px;
  font-weight: 700;
  margin-top: 8px;
  padding-top: 6px;
}

.group-chat__action-hint {
  color: #7a7285;
  font-size: 10px;
  font-weight: 400;
  margin-left: 6px;
}

.group-chat__action-devices {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2px;
}

.group-chat__action-device {
  align-items: center;
  display: flex;
  font-size: 11px;
}

.group-chat__action-fields {
  display: block;
  margin-top: 4px;
  width: 100%;
}

.group-chat__action-fields tbody {
  display: block;
  width: 100%;
}

.group-chat__action-fields tr {
  align-items: center;
  display: grid;
  gap: 2px 4px;
  grid-template-columns: minmax(0, 1fr) 16px minmax(120px, 1.35fr);
  padding: 3px 0;
  width: 100%;
}

.group-chat__action-fields td {
  font-size: 11px;
  min-width: 0;
  padding: 0;
  vertical-align: middle;
}

.group-chat__action-field--carried {
  color: #7a7285;
}

.group-chat__action-field-label {
  grid-column: 1 / -1;
  overflow-wrap: anywhere;
}

.group-chat__action-field-current {
  color: #7a7285;
  grid-column: 1;
  text-align: right;
  word-break: normal;
}

.group-chat__action-field-arrow {
  color: #7a7285;
  grid-column: 2;
  text-align: center;
}

.group-chat__action-field-new {
  grid-column: 3;
  word-break: normal;
}

.group-chat__action-field-new--changed {
  font-weight: 700;
}

.group-chat__action-field-input {
  max-width: 100%;
  min-width: 0;
  width: 100%;
}

.group-chat__action-typed {
  margin-top: 6px;
}

.group-chat__action-typed-input {
  margin-top: 4px;
  max-width: 140px;
}

.group-chat__action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.group-chat__action-error {
  color: #c10015;
  font-size: 11px;
  margin-top: 6px;
}

.group-chat__source-button {
  color: #4d5f53;
  font-size: 11px;
  max-width: 100%;
  min-height: 24px;
}

.group-chat__source-button :deep(.q-btn__content) {
  flex-wrap: nowrap;
  overflow: hidden;
}

.group-chat__source-button :deep(.q-btn__content span) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-chat__mention {
  font-weight: 700;
}

.group-chat__mention--propipe {
  color: #6d4c41;
}

/* ── Markdown rendering in bot messages ───────────── */
.group-chat__markdown :deep(p) {
  margin: 0 0 6px 0;
}
.group-chat__markdown :deep(p:last-child) {
  margin-bottom: 0;
}
.group-chat__markdown :deep(ul),
.group-chat__markdown :deep(ol) {
  margin: 4px 0;
  padding-left: 18px;
}
.group-chat__markdown :deep(li) {
  margin-bottom: 2px;
}
.group-chat__markdown :deep(strong) {
  font-weight: 600;
}
.group-chat__markdown :deep(code) {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 12px;
}
.group-chat__markdown :deep(table) {
  border-collapse: collapse;
  font-size: 12px;
  margin: 4px 0;
}
.group-chat__markdown :deep(th),
.group-chat__markdown :deep(td) {
  border: 1px solid #ccc;
  padding: 3px 6px;
}
.group-chat__markdown :deep(th) {
  background: rgba(0, 0, 0, 0.04);
  font-weight: 600;
}

.group-chat__thinking {
  font-style: italic;
  color: #666;
}

.thinking-dot1,
.thinking-dot2,
.thinking-dot3 {
  animation: thinking-blink 1.4s infinite;
}
.thinking-dot1 {
  animation-delay: 0s;
}
.thinking-dot2 {
  animation-delay: 0.2s;
}
.thinking-dot3 {
  animation-delay: 0.4s;
}

@keyframes thinking-blink {
  0%,
  20% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

/* ── Propipe autocomplete menu ────────────────────── */
.group-chat__at-menu {
  position: absolute;
  bottom: 100%;
  left: 8px;
  right: 8px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  margin-bottom: 4px;
}

.group-chat__at-option {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  border-radius: 8px;
  border: 2px solid transparent;
  transition: background-color 0.12s ease, border-color 0.12s ease,
    box-shadow 0.12s ease;
}

.group-chat__at-option:hover {
  background: #f0f0f0;
}

.group-chat__at-option--active {
  background: #e8f1ff;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  color: #0d47a1;
}

.group-chat__at-option--active strong {
  color: #0b3f8a;
}

.group-chat__at-option--active img {
  filter: drop-shadow(0 0 2px rgba(25, 118, 210, 0.6));
}

/* ── Reactions ────────────────────────────────────── */

/* ── Attachments ──────────────────────────────────── */
.group-chat__attachment {
  margin-top: 4px;
}

.group-chat__attachment-image {
  max-width: 280px;
  max-height: 200px;
  border-radius: 6px;
  display: block;
}

.group-chat__attachment-image-loading {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
  font-size: 12px;
  color: #888;
}

.group-chat__attachment-file {
  display: flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  padding: 4px 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  color: #1976d2;
}

.group-chat__attachment-file:hover {
  background: rgba(0, 0, 0, 0.1);
}

.group-chat__attachment-size {
  flex: 0 0 auto;
  color: #888;
  font-size: 11px;
}

.group-chat__attachment-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-chat__upload-preview {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #e3f2fd;
  border-radius: 6px;
  font-size: 12px;
  margin-bottom: 4px;
}

.group-chat__upload-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

.group-chat__attach-btn {
  cursor: pointer;
  opacity: 0.5;
  margin-right: 4px;
  transition: opacity 0.15s;
}

.group-chat__attach-btn:hover {
  opacity: 1;
}

/* ── Reactions ────────────────────────────────────── */
.group-chat__reactions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.group-chat__reaction-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.06);
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}

.group-chat__reaction-chip:hover {
  background: rgba(0, 0, 0, 0.12);
}

.group-chat__reaction-chip--mine {
  background: rgba(25, 118, 210, 0.15);
  outline: 1px solid rgba(25, 118, 210, 0.4);
}

.group-chat__reaction-count {
  font-size: 11px;
  color: #666;
  font-weight: 600;
}

.group-chat__reaction-add {
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  border-radius: 10px;
  font-size: 12px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s;
  background: rgba(0, 0, 0, 0.04);
  user-select: none;
}

.group-chat__bubble:hover .group-chat__reaction-add {
  opacity: 1;
}

/* ── Emoji picker ─────────────────────────────────── */
.group-chat__emoji-btn {
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  margin-right: 4px;
  user-select: none;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.group-chat__emoji-btn:hover {
  opacity: 1;
}

/* ── Image lightbox — styles intentionally in global block (teleported to body) ── */

.group-chat__emoji-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
}

.group-chat__emoji-picker {
  position: absolute;
  right: 8px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 2px;
  width: 210px;
}

.group-chat__emoji-option {
  font-size: 20px;
  text-align: center;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  user-select: none;
  transition: background 0.1s;
}

.group-chat__emoji-option:hover {
  background: #f0f0f0;
}
</style>

<style>
/* ── GroupChat select menus (global — teleported to body) ── */
.group-chat__select-menu {
  width: 280px !important;
  max-width: calc(100vw - 24px) !important;
  max-height: 240px !important;
  overflow-y: auto;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.group-chat__select-menu--participants {
  width: 240px !important;
}

.group-chat__select-menu .q-item {
  min-height: 34px;
  padding: 6px 10px;
  font-size: 13px;
}

.group-chat__select-menu .q-item__section,
.group-chat__select-menu .q-item__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── GroupChat image lightbox (global — teleported to body) ── */
.group-chat__lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.group-chat__lightbox {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 90vw;
  max-height: 90vh;
}

.group-chat__lightbox-img {
  max-width: 90vw;
  max-height: calc(90vh - 48px);
  object-fit: contain;
  border-radius: 6px;
  display: block;
}

.group-chat__lightbox-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 20px;
}

.group-chat__lightbox-name {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 4px;
}
</style>
